import React, { useEffect, useRef, useState } from 'react'
import { FormsDraftCard, TriageCard } from '../bostonIA/components/analysisCards'
import { aiCallAgentDashboard, aiCallDashboardImporterAgent, aiSetCostContext } from '../bostonIA/lib/ai'
import { lintDataJson, lintErrors, lintFixPrompt } from '../bostonIA/lib/dataJsonLint'
import { applyTransactionIntent, normalizeDataJson } from '../bostonIA/lib/dataJsonNormalize'
import { displayRemarkType, normalizeRemarkField } from '../bostonIA/lib/remarkSemantics'
import { validateRepairPreservation } from '../bostonIA/lib/dataJsonRepairGuard'
import { evaluateAgentGeneration, evaluateAgentInterpretation } from '../bostonIA/lib/agentGenerationEval'
import { reservationFixPrompt, reservationGenerationContract, reservationInterpretationGuide, validateReservationRequirements } from '../bostonIA/lib/reservationRequirements'
import { triageSst, triageToPromptBlock, buildFichaMd, irToHumanText, humanizeScriptName, getRequiredIRSummary, preprocessSst } from '../bostonIA/lib/triage'
import { makeT } from './i18n'
import { buildFormsDraft, formsDraftToPromptBlock } from '../bostonIA/lib/formsDraft'
import { normalizeReplacementChars } from '../bostonIA/lib/parsers'
import { state, uuidV4 } from '../bostonIA/lib/store'
import { decompileSsc } from '../bostonIA/lib/sscDecompiler'
import { csBuildPublishPayload, csBuildSingleFile, csExternalizeLargeCatalogs, csLoadRuntimeTemplates, csReindexTags, generateFakeBookingData } from '../bostonIA/lib/builder'
import DashboardLoader from './DashboardLoader'
import DiagnosticsModal from '../bostonIA/components/DiagnosticsModal'
import SstInterpreterPreview from '../bostonIA/components/SstInterpreterPreview'
import { checkCoverage } from '../bostonIA/lib/coverageCheck'
import { convertSstToBoston } from '../bostonIA/lib/sstConverter/convertSst'
import { buildDeterministicSstReviewIR } from '../bostonIA/lib/sstConverter/reviewIr'
import { buildSstConversionOptions, expandSstImportFiles } from '../bostonIA/lib/sstImportBundle'


/* Panel IA del dashboard publico.
   La API key no viaja al navegador; la publicacion directa es una accion explicita. */
const dashboardDryRun =
  new URLSearchParams(window.location.search).get('dryRun') === '1'
const dashboardImporterTimeoutMs = 8 * 60 * 1000

function debugAgent1Enabled() {
  try {
    return new URLSearchParams(window.location.search).get('debugAgent1') === '1'
  } catch (_) {
    return false
  }
}

function debugAgent1IR(label, ir) {
  if (!debugAgent1Enabled()) return
  console.debug(label, {
    keys: Object.keys(ir || {}),
    metaSummary: ir?.meta?.summary || '',
    rootSummary: ir?.summary || '',
    wrapped: Boolean(ir?.data || ir?.ir || ir?.result),
  })
}

function requireDashboardIRSummary(ir, label) {
  try {
    const summary = getRequiredIRSummary(ir)
    debugAgent1IR(label, ir)
    return summary
  } catch (error) {
    debugAgent1IR(label, ir)
    const keys = Object.keys(ir || {}).join(', ') || 'sin keys'
    throw new Error((error.message || 'Agente 1 no entregó la descripción funcional obligatoria en meta.summary.') + ' Keys IR: ' + keys)
  }
}

const mockDashboardDataJson = {
  title: 'TEST DASHBOARD MOCK',
  editCatalogues: false,
  reintentos: 0,
  sendCommands: false,
  validateActiveReservation: false,
  viewCatalogues: false,
  enable_api: true,
  forms: [
    {
      form: 'form1',
      title: 'Prueba Dashboard',
      title_visible: true,
      remarks: [
        {
          campoForm: 'nombre',
          label: 'Nombre',
          typedata: 'text',
          visible: true,
          mandatory: true,
          maxlength: 50,
          remark: '5.NOMBRE [nombre]',
          remark_mask: '5.NOMBRE [nombre]',
          block: 5,
          skip: true,
          processed: false,
          datasource: [],
        },
      ],
    },
  ],
}

/* Valida que el data.json sea EJECUTABLE: forms con campos O comandos directos.
   Un script SIN formulario es valido (ej. repricing automatico: onLoad + autoRun + ER). */
function dataJsonEjecutable(data) {
  if (!data) return false
  const tieneForms = Array.isArray(data.forms) && data.forms.some((f) => Array.isArray(f.remarks) && f.remarks.length)
  const tienePost = Array.isArray(data.postCommands) && data.postCommands.length > 0
  const tieneOnLoad = Array.isArray(data.onLoad) && data.onLoad.length > 0
  const tieneAutoRun = Boolean(data.autoRun)
  return tieneForms || tienePost || tieneOnLoad || tieneAutoRun
}

function comparisonMetrics(data, logicJson) {
  const forms = Array.isArray(data?.forms) ? data.forms : []
  const fields = forms.reduce((total, form) => total + (Array.isArray(form?.remarks) ? form.remarks.length : 0), 0)
  const commands = ['postCommands', 'onLoad']
    .reduce((total, key) => total + (Array.isArray(data?.[key]) ? data[key].length : 0), 0)
  const diagnostics = data ? lintDataJson(data) : []
  return {
    forms: forms.length,
    fields,
    commands,
    logicBlocks: Object.keys(logicJson?.blocks || {}).length,
    errors: lintErrors(diagnostics).length,
    warnings: diagnostics.filter((item) => item?.severity === 'warning' || item?.severity === 'warn').length,
  }
}

function comparisonJson(value) {
  return value ? JSON.stringify(value, null, 2) : 'No generado por este camino.'
}

function comparisonMockReservation() {
  const fake = generateFakeBookingData()
  return {
    booking_details: {
      record_locator: 'MOCK01',
      creation_timestamp: new Date().toISOString(),
      creation_agent_i_d: 'MOCK',
    },
    passenger_reservation: {
      reservation_header: { record_locator: 'MOCK01' },
      passengers: {
        passenger: [{
          name_association_id: '1.1',
          first_name: fake.fname,
          last_name: fake.lname,
          name_reference: fake.titulo,
        }],
      },
      segments: {
        segment: [{
          air: {
            departure_airport: fake.origin,
            arrival_airport: fake.dest,
            departure_date: fake.date,
            departure_time: fake.time,
            booking_class: fake.cls,
          },
        }],
      },
    },
    phone_numbers: { phone_number: [{ number: fake.phone }] },
    email_addresses: { email_address: [{ address: fake.email }] },
    preview: true,
    simulated: true,
  }
}

function isDashboardSourceSst(entry) {
  return Boolean(
    entry &&
      !entry.ssc &&
      !entry.decompiled &&
      !entry.isBinary &&
      /\.sst$/i.test(String(entry.name || '')),
  )
}

function cloneJSON(value) {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (_) {
    return value
  }
}

function isStrictSstDataJson(data, ir) {
  const mode =
    data?.executionMode ||
    data?.execution_mode ||
    data?.source_additional?.executionMode ||
    data?.source_additional?.execution_mode ||
    ir?.executionMode ||
    ir?.execution_mode ||
    ir?.source?.executionMode ||
    ir?.source?.execution_mode ||
    ''
  return String(mode).toLowerCase().trim() === 'sst_strict'
}

function irCommandPlan(ir) {
  if (Array.isArray(ir?.command_plan)) return ir.command_plan
  if (Array.isArray(ir?.commandPlan)) return ir.commandPlan
  if (Array.isArray(ir?.source_additional?.command_plan)) return ir.source_additional.command_plan
  if (Array.isArray(ir?.source_additional?.commandPlan)) return ir.source_additional.commandPlan
  return []
}

function preserveStrictSstCommandPlan(data, ir) {
  if (!data || typeof data !== 'object' || !isStrictSstDataJson(data, ir)) return data
  const plan = irCommandPlan(ir)
  if (!plan.length) return data
  const next = cloneJSON(data)
  const sourceAdditional =
    next.source_additional && typeof next.source_additional === 'object' && !Array.isArray(next.source_additional)
      ? { ...next.source_additional }
      : {}
  if (!Array.isArray(sourceAdditional.command_plan) || !sourceAdditional.command_plan.length) {
    sourceAdditional.command_plan = cloneJSON(plan)
  }
  next.source_additional = sourceAdditional
  return next
}

function aiFileIsSSC(name) {
  return /\.ssc$/i.test(String(name || ''))
}

function aiFileIsAuxiliary(name) {
  return /\.(csv|xls|xlsx|json)$/i.test(String(name || ''))
}

function fileBaseName(name) {
  return String(name || '').split(/[\\/]/).pop()
}

function comparableFileName(name) {
  return fileBaseName(name)
    .replace(/\.(sst|ssc|sbr|csv|xls|xlsx|txt|json)$/i, '')
    .trim()
    .toUpperCase()
}

function fileMatchesDependency(fileName, dependencyName) {
  const fileFull = String(fileName || '').trim().toUpperCase()
  const depFull = String(dependencyName || '').trim().toUpperCase()
  return (
    fileFull === depFull ||
    fileBaseName(fileFull) === fileBaseName(depFull) ||
    comparableFileName(fileName) === comparableFileName(dependencyName)
  )
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || ''
}

function dashboardCatalogSlug(value) {
  return String(value || 'catalogo')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48) || 'catalogo'
}

function dashboardCatalogHash(options) {
  const text = JSON.stringify(options || [])
  let hash = 0
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(36).slice(0, 6)
}

function dashboardCleanCatalogOption(value) {
  return String(value || '')
    .replace(/^[\s"'`´“”‘’.,;:|/-]*(?:[-*•]|\d+[.)])?\s*/g, '')
    .replace(/[\s"'`´“”‘’.,;:|]+$/g, '')
    .trim()
}

function dashboardSplitCatalogOptions(value) {
  const text = String(value || '')
    .replace(/\r/g, '\n')
    .replace(/\s+\|\s+/g, '|')
  return text
    .split(/\n|,|;/)
    .flatMap((part) => String(part).split('|'))
    .map(dashboardCleanCatalogOption)
    .filter((option) => option && option.length <= 90)
    .filter((option) => !/^(opciones?|lista|catalogo|catálogo|valores?)\s*:?\s*$/i.test(option))
}

function dashboardUniqueOptions(options) {
  const seen = new Set()
  const out = []
  ;(options || []).forEach((option) => {
    const clean = option && typeof option === 'object'
      ? {
          value: dashboardCleanCatalogOption(option.value ?? option.text ?? ''),
          text: dashboardCleanCatalogOption(option.text ?? option.value ?? ''),
        }
      : {
          value: dashboardCleanCatalogOption(option),
          text: dashboardCleanCatalogOption(option),
        }
    const key = `${clean.value}|${clean.text}`.toLowerCase()
    if ((!clean.value && !clean.text) || seen.has(key)) return
    seen.add(key)
    out.push({ value: clean.value || clean.text, text: clean.text || clean.value })
  })
  return out
}

function dashboardMergeCatalogRoots(...roots) {
  const merged = { version: 1, catalogs: {} }
  roots.forEach((root) => {
    const catalogs = root?.catalogs && typeof root.catalogs === 'object' && !Array.isArray(root.catalogs)
      ? root.catalogs
      : root && typeof root === 'object' && !Array.isArray(root)
        ? root
        : {}
    Object.entries(catalogs).forEach(([id, catalog]) => {
      if (catalog && typeof catalog === 'object' && Array.isArray(catalog.options)) {
        merged.catalogs[id] = catalog
      }
    })
  })
  return merged
}

function preprocessDashboardDescriptionCatalogs(text, options = {}) {
  const threshold = Number(options.threshold || 20)
  const original = String(text || '')
  const paragraphs = original.split(/\n\s*\n/g)
  const catalogsJson = { version: 1, catalogs: {} }
  const blocks = []
  let changed = false

  const isCatalogParagraph = (paragraph, optionsCount) =>
    /(opciones?|lista|listado|cat[aá]logo|valores?|select|combo)\s*:/i.test(paragraph) ||
    (optionsCount >= threshold && (paragraph.match(/[,|;]/g) || []).length >= threshold - 1)

  const compactedParagraphs = paragraphs.map((paragraph, paragraphIndex) => {
    const raw = String(paragraph || '')
    const colonIndex = raw.indexOf(':')
    const prefix = colonIndex >= 0 ? raw.slice(0, colonIndex + 1).trim() : ''
    const candidateText = colonIndex >= 0 && /(opciones?|lista|listado|cat[aá]logo|valores?|select|combo)/i.test(prefix)
      ? raw.slice(colonIndex + 1)
      : raw
    const optionsList = dashboardUniqueOptions(dashboardSplitCatalogOptions(candidateText))
    if (optionsList.length < threshold || !isCatalogParagraph(raw, optionsList.length)) return raw

    const labelSource = prefix.replace(/:$/, '') || `catalogo_${paragraphIndex + 1}`
    const ref = `cat_${dashboardCatalogSlug(labelSource)}_${dashboardCatalogHash(optionsList)}`
    catalogsJson.catalogs[ref] = {
      id: ref,
      label: labelSource,
      source: 'dashboard_description_preprocess',
      count: optionsList.length,
      options: optionsList,
    }
    blocks.push({
      ref,
      label: labelSource,
      count: optionsList.length,
      sample: optionsList.slice(0, 6).map((option) => option.text),
    })
    changed = true
    return [
      prefix || labelSource + ':',
      `[CATALOGO EXTERNO: ${ref}]`,
      `Opciones detectadas: ${optionsList.length}. Muestra: ${optionsList.slice(0, 6).map((option) => option.text).join(' | ')}.`,
      `Usar datasource_ref "${ref}" y NO expandir las opciones en el data.json.`,
    ].join('\n')
  })

  const compactText = compactedParagraphs.join('\n\n')
  const instruction = blocks.length
    ? '\n\nCATÁLOGOS EXTERNOS DETECTADOS POR PREPROCESAMIENTO:\n' +
      blocks.map((block) =>
        `- ${block.label}: datasource_ref="${block.ref}", opciones=${block.count}, muestra=${block.sample.join(' | ')}`,
      ).join('\n') +
      '\nRegla: para esos campos generá typedata:"select", datasource:[], datasource_ref con el id indicado y datasource_count. No inventes ni expandas el catálogo completo.'
    : ''

  const stats = {
    catalogs: blocks.length,
    optionsMoved: blocks.reduce((sum, block) => sum + block.count, 0),
    threshold,
  }

  if (changed) {
    console.log('[dashboard description preprocess catalogs]', stats, blocks.map((block) => block.ref))
  }

  return {
    text: compactText,
    instruction,
    catalogsJson,
    stats,
    enabled: changed,
  }
}

function applyDashboardDescriptionCatalogRefs(dataJson, preprocess) {
  if (!preprocess?.enabled || !preprocess.catalogsJson?.catalogs || !dataJson || typeof dataJson !== 'object') return dataJson
  const refs = Object.keys(preprocess.catalogsJson.catalogs || {})
  if (!refs.length) return dataJson
  const next = cloneJSON(dataJson)
  const matches = []
  const sstBlocks = Array.isArray(preprocess.blocks)
    ? preprocess.blocks.filter((block) => block && (block.variable || block.field))
    : []
  const sstRefs = new Set(sstBlocks.map((block) => block.ref).filter(Boolean))
  const catalogBySlug = refs.map((ref) => ({
    ref,
    catalog: preprocess.catalogsJson.catalogs[ref],
    labelSlug: dashboardCatalogSlug(preprocess.catalogsJson.catalogs[ref]?.label || ref),
  }))
  const chooseRef = (field) => {
    const fieldSlug = dashboardCatalogSlug([
      field?.campoForm,
      field?.tag,
      field?.id,
      field?.name,
      field?.label,
    ].filter(Boolean).join(' '))
    if (sstBlocks.length) {
      const exactMatch = sstBlocks.find((block) => {
        const aliases = [
          block.variable,
          block.field,
          String(block.variable || '').replace(/#D$/i, ''),
          String(block.field || '').replace(/#D$/i, ''),
        ].filter(Boolean).map((value) => dashboardCatalogSlug(value))
        return aliases.some((alias) => alias && (fieldSlug === alias || fieldSlug.split('_').includes(alias)))
      })
      return exactMatch ? exactMatch.ref : ''
    }
    if (refs.length === 1) return refs[0]
    const match = catalogBySlug.find((item) =>
      fieldSlug.includes(item.labelSlug) ||
      item.labelSlug.includes(fieldSlug) ||
      item.labelSlug.split('_').some((part) => part.length >= 4 && fieldSlug.includes(part)),
    )
    return match ? match.ref : ''
  }
  const visitField = (field, formName) => {
    if (!field || typeof field !== 'object') return
    const typedata = String(field.typedata || field.type || '').toLowerCase()
    if (typedata !== 'select') return
    const existingRef = String(field.datasource_ref || field.catalog_ref || '').trim()
    const chosenRef = chooseRef(field)
    const ref = refs.includes(existingRef) && (!sstRefs.has(existingRef) || existingRef === chosenRef) ? existingRef : chosenRef
    if (!ref) return
    field.datasource_ref = ref
    field.catalog_ref = ref
    field.datasource_count = preprocess.catalogsJson.catalogs[ref]?.count || preprocess.catalogsJson.catalogs[ref]?.options?.length || 0
    field.datasource = []
    matches.push({ form: formName, field: field.campoForm || field.tag || field.label || '', ref })
  }
  ;(Array.isArray(next.forms) ? next.forms : []).forEach((form, index) => {
    const formName = form?.form || form?.id || form?.title || `form${index + 1}`
    ;(Array.isArray(form?.remarks) ? form.remarks : []).forEach((field) => visitField(field, formName))
    ;(Array.isArray(form?.fields) ? form.fields : []).forEach((field) => visitField(field, formName))
  })
  ;(Array.isArray(next.actions) ? next.actions : []).forEach((action, index) => {
    const formName = action?.id || action?.label || `action${index + 1}`
    ;(Array.isArray(action?.fields) ? action.fields : []).forEach((field) => visitField(field, formName))
  })
  if (matches.length) {
    next.catalogs_file = next.catalogs_file || 'catalogs.json'
    next.catalogs_externalized = true
    console.log('[dashboard description catalog refs applied]', matches)
  }
  return next
}

function parseSstDefaultCatalogOptions(rawValue) {
  const raw = String(rawValue || '').trim()
  if (!raw || /^CSV:/i.test(raw)) return []
  return dashboardUniqueOptions(
    raw
      .split('|')
      .map((part) => {
        const clean = String(part || '').trim()
        if (!clean) return ''
        const eq = clean.indexOf('=')
        if (eq > 0) {
          const text = dashboardCleanCatalogOption(clean.slice(0, eq))
          const value = dashboardCleanCatalogOption(clean.slice(eq + 1))
          return value && text ? { value, text } : (value || text)
        }
        return clean
      }),
  )
}

function detectDashboardCsvDelimiter(content) {
  const firstLine = String(content || '').replace(/\r/g, '').split('\n').find((line) => line.trim()) || ''
  const candidates = [',', ';', '\t', '|']
  let best = ','
  let bestCount = 0
  candidates.forEach((delimiter) => {
    let count = 0
    let inQuotes = false
    for (let i = 0; i < firstLine.length; i += 1) {
      const ch = firstLine[i]
      const next = firstLine[i + 1]
      if (ch === '"') {
        if (inQuotes && next === '"') i += 1
        else inQuotes = !inQuotes
      } else if (ch === delimiter && !inQuotes) {
        count += 1
      }
    }
    if (count > bestCount) {
      best = delimiter
      bestCount = count
    }
  })
  return best
}

function parseDashboardCsvRows(content) {
  const rows = []
  const text = String(content || '').replace(/\r/g, '')
  const delimiter = detectDashboardCsvDelimiter(text)
  let row = []
  let cell = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    const next = text[i + 1]
    if (ch === '"') {
      if (inQuotes && next === '"') {
        cell += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }
    if (ch === delimiter && !inQuotes) {
      row.push(cell)
      cell = ''
      continue
    }
    if (ch === '\n' && !inQuotes) {
      row.push(cell)
      if (row.some((value) => String(value || '').trim() !== '')) rows.push(row)
      row = []
      cell = ''
      continue
    }
    cell += ch
  }
  row.push(cell)
  if (row.some((value) => String(value || '').trim() !== '')) rows.push(row)
  return rows.map((items) => items.map((item) => dashboardCleanCatalogOption(item)))
}

function parseDashboardCsvCatalogOptions(content) {
  const rows = parseDashboardCsvRows(content).filter((row) => row.some(Boolean))
  if (!rows.length) return []
  const header = rows[0].map((cell) => dashboardCatalogSlug(cell))
  const hasHeader = header.some((cell) => ['value', 'valor', 'codigo', 'code', 'id'].includes(cell)) ||
    header.some((cell) => ['text', 'texto', 'label', 'descripcion', 'nombre', 'name'].includes(cell))
  const dataRows = hasHeader ? rows.slice(1) : rows
  const valueIdx = hasHeader
    ? Math.max(
        header.findIndex((cell) => ['value', 'valor'].includes(cell)),
        header.findIndex((cell) => ['codigo', 'code', 'id'].includes(cell)),
      )
    : 0
  const textIdx = hasHeader
    ? Math.max(
        header.findIndex((cell) => ['text', 'texto', 'label'].includes(cell)),
        header.findIndex((cell) => ['descripcion', 'nombre', 'name'].includes(cell)),
      )
    : (rows[0].length > 1 ? 1 : 0)
  return dashboardUniqueOptions(dataRows.map((row) => {
    const value = row[valueIdx >= 0 ? valueIdx : 0] || row[0] || ''
    const text = row[textIdx >= 0 ? textIdx : (row.length > 1 ? 1 : 0)] || value
    return { value, text }
  }))
}

function findDashboardAuxiliaryFile(auxiliaryFiles = [], dependencyName = '') {
  return (auxiliaryFiles || []).find((entry) => fileMatchesDependency(entry?.name, dependencyName))
}

function preprocessDashboardSstCatalogs(source, fileName = '', options = {}) {
  const threshold = Number(options.threshold || 20)
  const original = preprocessSst(normalizeReplacementChars(String(source || '')))
  const catalogsJson = { version: 1, catalogs: {} }
  const blocks = []
  const usedAuxiliaryFiles = new Set()
  const rx = /DEFAULT\s*\[([^\]]+#D)\]\s*=\s*("[^"]*"|'[^']*'|[^\r\n]+)/gi
  const text = original.replace(rx, (full, variable, rawValue) => {
    let value = String(rawValue || '').trim()
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1)
    }
    const csvMatch = value.match(/^CSV:\s*(.+)$/i)
    const csvName = csvMatch ? csvMatch[1].trim() : ''
    const csvFile = csvName ? findDashboardAuxiliaryFile(options.auxiliaryFiles, csvName) : null
    const optionsList = csvFile
      ? parseDashboardCsvCatalogOptions(csvFile.content)
      : parseSstDefaultCatalogOptions(value)
    if (optionsList.length < threshold) return full
    const ref = `cat_${dashboardCatalogSlug(variable)}_${dashboardCatalogHash(optionsList)}`
    catalogsJson.catalogs[ref] = {
      id: ref,
      label: variable,
      field: variable.replace(/#D$/i, ''),
      source: csvFile ? 'dashboard_sst_csv_preprocess' : 'dashboard_sst_default_preprocess',
      fileName,
      csvFileName: csvFile?.name || '',
      count: optionsList.length,
      options: optionsList,
    }
    if (csvFile?.name) usedAuxiliaryFiles.add(csvFile.name)
    blocks.push({
      ref,
      variable,
      field: variable.replace(/#D$/i, ''),
      csvFileName: csvFile?.name || '',
      count: optionsList.length,
      sample: optionsList.slice(0, 6).map((option) => option.text),
    })
    return [
      `DEFAULT [${variable}]="CATALOG_REF:${ref}"`,
      `; CATALOGO EXTERNO ${ref}: ${optionsList.length} opciones extraidas por preprocesamiento. No expandir en IR/data.json.`,
    ].join('\n')
  })
  const instruction = blocks.length
    ? '\n\nCATÁLOGOS EXTERNOS DETECTADOS POR PREPROCESAMIENTO SST:\n' +
      blocks.map((block) =>
        `- ${block.variable}: datasource_ref="${block.ref}", opciones=${block.count}, muestra=${block.sample.join(' | ')}`,
      ).join('\n') +
      '\nRegla: conserva el campo como select, usa datasource:[], datasource_ref/catalog_ref con el id indicado y datasource_count. No expandas el catálogo completo.'
    : ''
  const stats = {
    catalogs: blocks.length,
    optionsMoved: blocks.reduce((sum, block) => sum + block.count, 0),
    threshold,
  }
  if (blocks.length) {
    console.log('[dashboard sst preprocess catalogs]', fileName, stats, blocks.map((block) => block.ref))
  }
  return {
    text,
    instruction,
    catalogsJson,
    stats,
    blocks,
    usedAuxiliaryFiles: [...usedAuxiliaryFiles],
    enabled: blocks.length > 0,
  }
}

function preprocessDashboardSstCatalogsForEntries(entries = []) {
  const compactByName = {}
  const roots = []
  const instructions = []
  const blocks = []
  const usedAuxiliaryFiles = new Set()
  const stats = { catalogs: 0, optionsMoved: 0, threshold: 20 }
  const auxiliaryFiles = (entries || []).filter((entry) => entry && aiFileIsAuxiliary(entry.name))
  ;(entries || []).forEach((entry) => {
    if (!entry || entry.ssc || entry.role === 'dependency' || aiFileIsAuxiliary(entry.name)) {
      compactByName[entry?.name || ''] = entry?.content || ''
      return
    }
    const pre = preprocessDashboardSstCatalogs(entry.content, entry.name, { auxiliaryFiles })
    compactByName[entry.name] = pre.enabled ? pre.text : entry.content
    if (pre.enabled) {
      roots.push(pre.catalogsJson)
      instructions.push(pre.instruction)
      blocks.push(...pre.blocks)
      ;(pre.usedAuxiliaryFiles || []).forEach((name) => usedAuxiliaryFiles.add(name))
      stats.catalogs += pre.stats.catalogs
      stats.optionsMoved += pre.stats.optionsMoved
    }
  })
  auxiliaryFiles.forEach((entry) => {
    if (usedAuxiliaryFiles.has(entry.name)) {
      compactByName[entry.name] = `ARCHIVO CSV EXTERNALIZADO: ${entry.name}\nContenido movido a catalogs.json; no expandir filas en IR/data.json.`
    }
  })
  return {
    compactByName,
    catalogsJson: dashboardMergeCatalogRoots(...roots),
    instruction: instructions.filter(Boolean).join('\n'),
    stats,
    blocks,
    usedAuxiliaryFiles: [...usedAuxiliaryFiles],
    enabled: stats.catalogs > 0,
  }
}

function dashboardSabreToken(fallbackToken = '') {
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get('token') || params.get('sabreToken') || params.get('ath') || fallbackToken || ''
  } catch (_) {
    return fallbackToken || ''
  }
}

async function fetchDashboardRuntimeSession({ token, apiBaseUrl, apiKey }) {
  const cleanToken = String(token || '').trim()
  const baseUrl = String(apiBaseUrl || '').replace(/\/$/, '')
  if (!cleanToken) throw new Error('Falta token Sabre para obtener la sesion.')
  if (!baseUrl) throw new Error('Falta URL publica para obtener la sesion.')

  const response = await fetch(`${baseUrl}/customScripts/session`, {
    method: 'GET',
    headers: {
      Accept: 'application/json, text/plain, */*',
      token: cleanToken,
      'x-api-key': apiKey || '',
    },
    cache: 'no-store',
  })
  const text = await response.text()
  let data = null
  try {
    data = text ? JSON.parse(text) : null
  } catch (_) {
    data = text
  }
  if (!response.ok) {
    throw new Error((data && data.message) || `No se pudo obtener sesion (${response.status}).`)
  }
  const session = {
    username: firstValue(data?.username, data?.user),
    parent_pcc: firstValue(data?.parent_pcc, data?.parentPcc, data?.parentPCC),
    pcc: firstValue(data?.pcc, data?.PCC),
  }
  if (!session.username || !session.parent_pcc || !session.pcc) {
    throw new Error('La sesion no contiene username, parent_pcc o pcc.')
  }
  return session
}

async function dashboardAIAdminFetch({ apiBaseUrl, apiKey, token, resource = 'custom-scripts', action, payload }) {
  const cleanToken = String(token || '').trim()
  const baseUrl = String(apiBaseUrl || '').replace(/\/$/, '')
  if (!cleanToken) throw new Error('Falta token Sabre para ejecutar la accion.')
  if (!baseUrl) throw new Error('Falta URL publica para ejecutar la accion.')
  console.log('[dashboard modal] using admin session', false)
  console.log('[dashboard modal] source', payload?.source)

  const response = await fetch(`${baseUrl}/admin/ai?resource=${encodeURIComponent(resource)}&action=${encodeURIComponent(action)}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      token: cleanToken,
      'x-api-key': apiKey || '',
    },
    body: JSON.stringify(payload || {}),
  })
  const text = await response.text()
  let data = null
  try {
    data = text ? JSON.parse(text) : null
  } catch (_) {
    data = { message: text }
  }
  if (!response.ok || data?.ok === false) {
    throw new Error(data?.message || data?.error || `No se pudo ejecutar ${action} (${response.status}).`)
  }
  return data
}

function arrayBufferBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return window.btoa(binary)
}

function dashboardUploadSourceFile(file, buffer, options) {
  return dashboardAIAdminFetch({
    ...options,
    resource: 'source-files',
    action: 'upload',
    payload: {
      source: 'dashboard_file_import',
      token: options.token,
      uploadId: options.uploadId,
      importId: options.importId,
      originalFilename: file.name,
      fileRole: options.fileRole || '',
      contentType: file.type || 'application/octet-stream',
      contentBase64: arrayBufferBase64(buffer),
      targetEnv: options.targetEnv || 'stg',
    },
  })
}

function dashboardSaveDraftReview(payload, options) {
  return dashboardAIAdminFetch({ ...options, action: 'save-draft', payload })
}

function dashboardValidatePublish(payload, options) {
  return dashboardAIAdminFetch({ ...options, action: 'validate-publish', payload })
}

function dashboardPublishRelease(payload, options) {
  return dashboardAIAdminFetch({ ...options, action: 'publish-release', payload })
}

function publishTargetEnvFromValue(value) {
  const raw = String(value || '').trim().toLowerCase()
  if (!raw) return ''
  if (raw === 'prod' || raw === 'production') return 'prod'
  if (raw === 'stg' || raw === 'stage' || raw === 'staging') return 'stg'
  if (/^https?:\/\/stg[.-]/.test(raw) || raw.includes('//stg.') || raw.includes('-stg.')) return 'stg'
  if (raw.includes('boston.basset.ws') && !raw.includes('stg.')) return 'prod'
  return ''
}

function resolveDashboardPublishTargetEnv(...values) {
  for (const value of values) {
    const env = publishTargetEnvFromValue(value)
    if (env) return env
  }
  return 'stg'
}

function scriptName(value) {
  return String(value || 'script_ia')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'script_ia'
}

function dashboardVisibleScriptName(value, fallback = 'Script IA') {
  const clean = String(value || fallback || 'Script IA').replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim()
  return Array.from(clean || 'Script IA').slice(0, 100).join('')
}

function truncateDashboardDescription(value, maxLength = 255) {
  return Array.from(String(value || '').trim()).slice(0, maxLength).join('')
}

function normalizeGeneratedScript({ title, data, ir, ficha, origen, session, catalogsJson, logicJson }) {
  const scriptUuid = data?._uuid || uuidV4()
  const cleanDataBase = {
    ...(data || {}),
    _uuid: scriptUuid,
    _session: {
      username: session?.username || '',
      parentPcc: session?.parent_pcc || '',
      parent_pcc: session?.parent_pcc || '',
      pcc: session?.pcc || '',
    },
  }
  const catalogized = csExternalizeLargeCatalogs(cleanDataBase, { existingCatalogs: catalogsJson })
  const cleanData = catalogized.data
  if (catalogized.stats.refsCreated > 0) {
    console.log('[dashboard catalog externalize]', catalogized.stats)
  }
  const name = scriptName(cleanData.name || cleanData.title || title)
  return {
    title: cleanData.title || title || name,
    name,
    uuid: scriptUuid,
    data: cleanData,
    catalogsJson: catalogized.catalogsJson,
    catalogStats: catalogized.stats,
    ir: ir || null,
    ficha: ficha || null,
    origen: truncateDashboardDescription(origen),
    session,
    logicJson: logicJson || { postCommands: cleanData.postCommands || [] },
  }
}

function applyDashboardScriptToBuilder(draft) {
  const data = draft.data || {}
  try {
    state.cs.raw = JSON.parse(JSON.stringify(data))
    if (state.cs.raw && state.cs.raw._uuid !== undefined) delete state.cs.raw._uuid
    if (state.cs.raw && state.cs.raw._session !== undefined) delete state.cs.raw._session
  } catch (_) {
    state.cs.raw = null
  }
  state.cs.uuid = draft.uuid
  state.cs.name = draft.name || scriptName(data.name || data.title)
  state.cs.title = data.title || draft.title || state.cs.name
  state.cs.description = truncateDashboardDescription(draft.origen)
  state.cs.validateActiveReservation = data.validateActiveReservation !== false
  state.cs.sendCommands = !!data.sendCommands
  state.cs.orderExecute = data.orderExecute !== false
  state.cs.editCatalogues = !!data.editCatalogues
  state.cs.reintentos = data.reintentos || 0
  state.cs.targetEnv = data.targetEnv || 'stg'
  state.cs.forms =
    Array.isArray(data.forms) && data.forms.length
      ? data.forms.map((form) => ({
          ...form,
          form: form.form || 'form1',
          title: form.title || 'FORM',
          title_visible: form.title_visible !== false,
          remarks: (form.remarks || []).map((field) => ({
            ...normalizeRemarkField(field),
            campoForm: field.campoForm || '',
            label: field.label || '',
            mandatory: !!field.mandatory,
            remark: field.remark || '',
            remark_mask: field.remark_mask || '',
            source_additional: field.source_additional || '',
            typedata: field.typedata || 'text',
            typeremark: displayRemarkType(field),
            datasource: field.datasource || [],
          })),
        }))
      : [{ form: 'form1', title: 'FORMULARIO', title_visible: true, remarks: [] }]
  state.cs.postCommands = data.postCommands || []
  state.cs.logicJson = draft.logicJson || { postCommands: data.postCommands || [] }
  state.cs.catalogsJson = draft.catalogsJson || null
  state.cs.handlerJs = null
  state.cs.fichaMd = draft.ficha || null
  state.cs.url = ''
  state.cs.config = {}
  state.cs.persisted = false
  state.cs.isSaved = false
  csReindexTags()
}

async function withDashboardRuntimeConfig(runtimeApiBaseUrl, runtimeApiKey, env, task) {
  const previousConfig = state.config
  state.config = {
    ...(state.config || {}),
    publicApiBaseUrl: runtimeApiBaseUrl || state.config?.publicApiBaseUrl,
    apiBaseUrl: runtimeApiBaseUrl || state.config?.apiBaseUrl,
    apiKey: runtimeApiKey || state.config?.apiKey,
    publicApiKey: runtimeApiKey || state.config?.publicApiKey,
    bostonAkey: runtimeApiKey || state.config?.bostonAkey,
    env: env || state.config?.env || 'stg',
  }
  try {
    return await task()
  } finally {
    state.config = previousConfig
  }
}

async function resolveDashboardSession({ runtimeSession, runtimeToken, runtimeApiBaseUrl, runtimeApiKey }) {
  if (runtimeSession?.username && runtimeSession?.parentPcc && runtimeSession?.pcc) {
    return {
      username: String(runtimeSession.username),
      parent_pcc: String(runtimeSession.parentPcc),
      pcc: String(runtimeSession.pcc),
    }
  }
  return fetchDashboardRuntimeSession({
    token: runtimeToken,
    apiBaseUrl: runtimeApiBaseUrl,
    apiKey: runtimeApiKey,
  })
}

function dashboardDraftPayload(draft, { source, status = 0, feedback = '' }) {
  const session = draft.session || {}
  const cleanFeedback = String(feedback || '').trim()
  const history = {
    source,
    ...(cleanFeedback ? { feedback: cleanFeedback } : {}),
    target: {
      assignmentType: 'user',
      username: session.username,
      parent_pcc: session.parent_pcc,
      pcc: session.pcc,
      preferred_language: 'es',
    },
  }
  return {
    script: {
      uuid: draft.uuid,
      name: dashboardVisibleScriptName(draft.title, draft.name),
      description: truncateDashboardDescription(source === 'dashboard_review' && cleanFeedback ? cleanFeedback : draft.origen),
      url: '',
      status,
    },
    config: {
      dataJson: draft.data,
      irJson: draft.ir || null,
      logicJson: draft.logicJson || { postCommands: draft.data?.postCommands || [] },
      catalogsJson: draft.catalogsJson || null,
      handlerJs: null,
      fichaMd: draft.ficha || null,
      history,
    },
    reviewId: source === 'dashboard_review' ? `rev_${Date.now().toString(36)}` : undefined,
    scriptUuid: draft.uuid,
    source,
    assignmentType: 'user',
    feedback: cleanFeedback || undefined,
    reviewFeedback: cleanFeedback || undefined,
    preferredLanguage: 'es',
    history,
    username: session.username,
    parentPcc: session.parent_pcc,
    parent_pcc: session.parent_pcc,
    pcc: session.pcc,
    status,
    updatedUser: session.username || 'dashboard',
    sourceFileIds: (draft.sourceFiles || []).map((file) => file?.id).filter(Boolean),
  }
}

async function saveDashboardReview(draft, options, feedback = '') {
  const response = await dashboardSaveDraftReview(dashboardDraftPayload(draft, { source: 'dashboard_review', status: 0, feedback }), options)
  const saved = response?.data && typeof response.data === 'object' ? response.data : response
  if (saved?.ok === false) {
    throw new Error(saved.message || 'No se pudo guardar en Revision Dev.')
  }
  const reviewId = String(saved?.reviewId || '').trim()
  if (!reviewId) {
    throw new Error('La API guardó el borrador, pero no creó la revisión DEV (respuesta sin reviewId). Verifica que api-bostonflows de este ambiente tenga desplegado el soporte dashboard_review.')
  }
  return { ok: true, id: reviewId, scriptUuid: saved?.scriptUuid || draft.uuid, via: 'db' }
}

function cacheDashboardReview(draft, saved, feedback = '') {
  if (!draft || !saved?.id) return
  try {
    const key = 'boston_dev_queue_v1'
    const queue = JSON.parse(localStorage.getItem(key) || '[]')
    const dataJson = JSON.stringify(draft.data || {}, null, 2)
    const files = {
      'data.json': dataJson,
      'logic.json': JSON.stringify(draft.logicJson || { postCommands: draft.data?.postCommands || [] }, null, 2),
      'feedback.txt': String(feedback || ''),
    }
    if (draft.ir) files['ir.json'] = JSON.stringify(draft.ir, null, 2)
    if (draft.catalogsJson) files['catalogs.json'] = JSON.stringify(draft.catalogsJson, null, 2)
    const item = {
      id: saved.id,
      ts: new Date().toISOString(),
      title: draft.title || draft.name || '(sin título)',
      feedback: String(feedback || ''),
      estado: 'en_revision',
      data: draft.data || {},
      ir: draft.ir || null,
      files,
      original: dataJson,
      origin: 'dashboard_review',
      source: 'dashboard_review',
      scriptUuid: saved.scriptUuid || draft.uuid,
      createdUser: draft.session?.username || '',
      username: draft.session?.username || '',
      parentPcc: draft.session?.parent_pcc || '',
      pcc: draft.session?.pcc || '',
      history: {
        source: 'dashboard_review',
        target: {
          assignmentType: 'user',
          username: draft.session?.username || '',
          parent_pcc: draft.session?.parent_pcc || '',
          pcc: draft.session?.pcc || '',
        },
        events: [{ ts: new Date().toISOString(), action: 'enviado a revisión' }],
      },
      sourceFiles: draft.sourceFiles || [],
      sourceFileIds: (draft.sourceFiles || []).map((file) => file?.id).filter(Boolean),
      _syncState: 'db',
    }
    const index = queue.findIndex((entry) => String(entry?.id || '') === saved.id)
    if (index >= 0) queue[index] = { ...queue[index], ...item }
    else queue.push(item)
    localStorage.setItem(key, JSON.stringify(queue.slice(-100)))
    window.dispatchEvent(new CustomEvent('boston-dev-review-queue-changed'))
  } catch (_) {}
}

function assertDashboardPublishPayload(payload, token) {
  const files = payload?.files || []
  const byPath = Object.fromEntries(files.map((file) => [file.path, file.content]))
  const requiredFiles = ['index.html', 'app.js', 'api.js', 'styles.css', 'data.json', 'logic.json', 'config_app.json']
  const missing = requiredFiles.filter((path) => !byPath[path])
  if (missing.length > 0) {
    throw new Error(`Paquete incompleto para publicar: faltan ${missing.join(', ')}`)
  }
  const configApp = JSON.parse(byPath['config_app.json'])
  if (configApp?.config?.enableRuntimeApi !== true) {
    throw new Error('config_app.json debe publicar con enableRuntimeApi=true.')
  }
  if (!String(byPath['app.js'] || '').includes('_rtHasEffectiveScriptOverlayConfig')) {
    throw new Error('app.js del paquete está obsoleto y no puede aplicar configuración por usuario. Refresca el Admin local y vuelve a publicar.')
  }
  if (!String(byPath['app.js'] || '').includes('const rawMask = r.remark_mask ||') || !String(byPath['app.js'] || '').includes('const normalizedRemarkType = _rtNormalizeRemarkType(r.typeremark)')) {
    throw new Error('app.js del paquete está obsoleto y no recompone el tipo de remark antes de enviar. Refresca el Admin local y vuelve a publicar.')
  }
  const dataJson = JSON.parse(byPath['data.json'])
  if (String(dataJson?.executionMode || '').toLowerCase().trim() === 'sst_interpreter_v1') {
    const logicJson = JSON.parse(byPath['logic.json'])
    if (!logicJson?.entryBlock || !logicJson?.blocks || !Object.keys(logicJson.blocks).length) {
      throw new Error('El paquete SST necesita logic.json con entryBlock y blocks antes de publicarse.')
    }
    if (!String(byPath['app.js'] || '').includes('BostonSstInterpreterV1')) {
      throw new Error('El paquete SST no contiene el intérprete en app.js.')
    }
  }
  if (token && files.some((file) => String(file.content || '').includes(token))) {
    throw new Error('El paquete de publicacion no debe contener el token Sabre.')
  }
}

async function buildDashboardDirectPublishPayload(draft, runtimeApiBaseUrl, runtimeApiKey, runtimeToken) {
  applyDashboardScriptToBuilder(draft)
  const env = resolveDashboardPublishTargetEnv(runtimeApiBaseUrl, window.location.href, draft.data?.targetEnv)
  const payload = await withDashboardRuntimeConfig(runtimeApiBaseUrl, runtimeApiKey, env, () => csBuildPublishPayload())
  const nextPayload = {
    ...payload,
    targetEnv: env,
    source: 'dashboard_direct',
    scriptUuid: draft.uuid,
    assignmentType: 'user',
    username: draft.session.username,
    parentPcc: draft.session.parent_pcc,
    pcc: draft.session.pcc,
    preferredLanguage: 'es',
    updatedUser: draft.session.username || 'dashboard',
    sourceFileIds: (draft.sourceFiles || []).map((file) => file?.id).filter(Boolean),
    script: {
      ...payload.script,
      uuid: draft.uuid,
      name: dashboardVisibleScriptName(draft.title, draft.name),
      status: 1,
      targetEnv: env,
    },
  }
  assertDashboardPublishPayload(nextPayload, runtimeToken)
  return nextPayload
}

function esProcesoDetenido(error) {
  if (!error) return false
  if (error.name === 'AbortError') return true
  return /detenido|aborted|abort/i.test(String(error.message || ''))
}

/* --- M2: borrador temporal del constructor persistido en localStorage ---
   Se separa por ambiente/agencia/PCC/usuario, caduca y se limpia cuando la
   generación desde descripción termina correctamente. */
const PANEL_DRAFT_PREFIX = 'boston_ia_panel_draft_v2'
const PANEL_DRAFT_LEGACY_PREFIX = 'boston_ia_panel_draft_v1'
const PANEL_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000
const PANEL_DRAFT_DEBOUNCE_MS = 600

function panelDraftPart(value, fallback) {
  return encodeURIComponent(String(value || fallback).trim().toLowerCase())
}

function panelDraftScope(runtimeSession, runtimeApiBaseUrl) {
  return [
    resolveDashboardPublishTargetEnv(runtimeApiBaseUrl, window.location.href),
    runtimeSession?.parentPcc || runtimeSession?.parent_pcc || 'no-parent-pcc',
    runtimeSession?.pcc || 'no-pcc',
    runtimeSession?.username || 'anon',
  ].map((value) => panelDraftPart(value, 'unknown')).join(':')
}

function panelDraftKey(scope) {
  return PANEL_DRAFT_PREFIX + ':' + scope
}

function legacyPanelDraftKey(username) {
  return PANEL_DRAFT_LEGACY_PREFIX + ':' + panelDraftPart(username, 'anon')
}

function readPanelDraft(key) {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const snap = JSON.parse(raw)
    const updatedAt = Number(snap?.updatedAt || snap?.ts || 0)
    if (!updatedAt || Date.now() - updatedAt > PANEL_DRAFT_TTL_MS) {
      window.localStorage.removeItem(key)
      return null
    }
    return snap
  } catch (_) { return null }
}

function loadPanelDraft(scope, username) {
  const current = readPanelDraft(panelDraftKey(scope))
  if (current) return current

  const legacyKey = legacyPanelDraftKey(username)
  const legacy = readPanelDraft(legacyKey)
  if (legacy) {
    savePanelDraft(scope, legacy)
    try { window.localStorage.removeItem(legacyKey) } catch (_) { /* no-op */ }
  }
  return legacy
}

function savePanelDraft(scope, snap) {
  try {
    window.localStorage.setItem(panelDraftKey(scope), JSON.stringify({ ...snap, updatedAt: Date.now() }))
  } catch (_) { /* quota */ }
}

function clearPanelDraft(scope, username) {
  try {
    window.localStorage.removeItem(panelDraftKey(scope))
    window.localStorage.removeItem(legacyPanelDraftKey(username))
  } catch (_) { /* no-op */ }
}
function panelDraftIsEmpty(snap) {
  if (!snap) return true
  return !(snap.desc || snap.descPreprocess || snap.importIR || snap.chat || snap.importSummary || snap.importDescription || snap.resultado)
}

function BostonIAPanel({ runtimeSession = null, runtimeToken = '', runtimeApiBaseUrl = '', runtimeApiKey = '', onPublished = null, onOpenPublished = null, onReviewSent = null, t = null, lang = 'es', advancedMode = false }) {
  const tr = t || makeT(lang)
  const fileRef = useRef(null)
  const sourceImportIdRef = useRef('')
  const abortRef = useRef(null)
  const processBusyRef = useRef(false)
  const params = new URLSearchParams(window.location.search)
  const mockModeEnabled = params.get('mock') === '1'
  const sabreToken = dashboardSabreToken(runtimeToken)
  // Varios archivos: si un script encadena a otro (CHAIN/SPAWN), se suben todos
  // y entran en un MISMO análisis. files[0] es el principal.
  const [files, setFiles] = useState([]) // [{ name, content, triage, ssc, decompiled, formsDraft, role }]
  const file = files.find((entry) => entry.role !== 'dependency') || null
  const [desc, setDesc] = useState('')
  const [descPreprocess, setDescPreprocess] = useState(null)
  const [importIR, setImportIR] = useState(null)
  const [importSummary, setImportSummary] = useState('')
  const [importDescription, setImportDescription] = useState('')
  const [fase, setFase] = useState('') // '' | 'agente1' | 'chat' | 'agente3' | 'listo' | 'error'
  const [resultado, setResultado] = useState(null) // { title, nivel }
  const [error, setError] = useState('')
  // Chat de interpretación: la IA SIEMPRE interpreta antes de generar y se puede
  // corregir conversando con el agente. Al confirmar, el texto va al constructor.
  const [chat, setChat] = useState(null) // { source: 'file'|'desc', ir, fileName, messages: [{role:'agent'|'user', text}] }
  const [chatInput, setChatInput] = useState('')

  const [previewDraft, setPreviewDraft] = useState(null)
  const [previewBusy, setPreviewBusy] = useState('')
  const [sstPreviewBusy, setSstPreviewBusy] = useState(false)
  const [reviewFeedback, setReviewFeedback] = useState('')
  const [reviewConfirmOpen, setReviewConfirmOpen] = useState(false)
  const [drafts, setDrafts] = useState([]) // borradores generados sin enviar (para re-ver el preview)
  const [showAnalysis, setShowAnalysis] = useState(false) // modal con el detalle del analisis del .sst
  const [diagOpen, setDiagOpen] = useState(false)
  const [pendingDependencyUpload, setPendingDependencyUpload] = useState(null)
  const [comparison, setComparison] = useState(null)
  const [comparisonTab, setComparisonTab] = useState('preview')
  const [comparisonRealConfirmOpen, setComparisonRealConfirmOpen] = useState(false)
  const [comparisonRealAccepted, setComparisonRealAccepted] = useState(false)

  // --- M2: restaurar/guardar borrador temporal del constructor (localStorage) ---
  const draftUser = (runtimeSession && runtimeSession.username) || ''
  const draftScope = panelDraftScope(runtimeSession, runtimeApiBaseUrl)
  const restoredDraftScopeRef = useRef('')

  function currentPanelDraftSnapshot() {
    return { desc, descPreprocess, importIR, importSummary, importDescription, chat, resultado }
  }

  function persistPanelDraftNow() {
    const snap = currentPanelDraftSnapshot()
    if (panelDraftIsEmpty(snap)) clearPanelDraft(draftScope, draftUser)
    else savePanelDraft(draftScope, snap)
  }

  function clearGeneratedDescriptionDraft() {
    clearPanelDraft(draftScope, draftUser)
    setDesc('')
    setDescPreprocess(null)
  }

  useEffect(() => {
    if (restoredDraftScopeRef.current === draftScope) return
    restoredDraftScopeRef.current = draftScope
    const snap = loadPanelDraft(draftScope, draftUser)
    if (panelDraftIsEmpty(snap)) return
    if (snap.desc) setDesc(snap.desc)
    if (snap.descPreprocess) setDescPreprocess(snap.descPreprocess)
    if (snap.importIR) setImportIR(snap.importIR)
    if (snap.importSummary) setImportSummary(snap.importSummary)
    if (snap.importDescription) setImportDescription(snap.importDescription)
    if (snap.chat) setChat(snap.chat)
    if (snap.resultado) setResultado(snap.resultado)
    console.debug('[dashboard draft] borrador restaurado')
  }, [draftScope, draftUser])

  useEffect(() => {
    if (restoredDraftScopeRef.current !== draftScope) return undefined
    const snap = { desc, descPreprocess, importIR, importSummary, importDescription, chat, resultado }
    const timer = window.setTimeout(() => {
      if (panelDraftIsEmpty(snap)) clearPanelDraft(draftScope, draftUser)
      else savePanelDraft(draftScope, snap)
    }, PANEL_DRAFT_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draftScope, draftUser, desc, descPreprocess, importIR, importSummary, importDescription, chat, resultado])

  function startAbortableProcess() {
    if (processBusyRef.current) return null
    processBusyRef.current = true
    const controller = new AbortController()
    abortRef.current = controller
    return controller.signal
  }

  function finishAbortableProcess() {
    processBusyRef.current = false
    abortRef.current = null
  }

  function detenerProceso() {
    try {
      if (abortRef.current) abortRef.current.abort()
    } catch (_) { /* no-op */ }
  }

  useEffect(() => () => {
    try {
      if (abortRef.current) abortRef.current.abort()
    } catch (_) { /* no-op */ }
    processBusyRef.current = false
    abortRef.current = null
  }, [])

  async function callDashboardAgent(agent, prompt, expectJson = true, generationMode = 'natural') {
    console.log('[dashboard ai] sabre token present', Boolean(sabreToken))
    console.log('[dashboard ai] using admin token', false)
    return aiCallAgentDashboard(agent, prompt, expectJson, {
      token: sabreToken,
      apiBaseUrl: runtimeApiBaseUrl,
      apiKey: runtimeApiKey,
      signal: abortRef.current ? abortRef.current.signal : undefined,
      generationMode,
    })
  }

  async function callDashboardFileAgent(agent, prompt, expectJson = true, generationMode = 'import', referenceIr = null) {
    if (isDashboardSourceSst(file) && ['agent1_sst', 'agent3_gen'].includes(agent)) {
      console.error('[dashboard-import] blocked ai request reason=deterministic-sst', agent, file?.name || '')
      throw new Error('Los agentes 1 y 3 están deshabilitados para importaciones SST; la interpretación y conversión deben ejecutarse localmente.')
    }
    console.log('[dashboard file import] sabre token present', Boolean(sabreToken))
    console.log('[dashboard file import] using admin token', false)
    return aiCallDashboardImporterAgent(agent, prompt, expectJson, {
      token: sabreToken,
      apiBaseUrl: runtimeApiBaseUrl,
      apiKey: runtimeApiKey,
      timeoutMs: dashboardImporterTimeoutMs,
      signal: abortRef.current ? abortRef.current.signal : undefined,
      generationMode,
      referenceIr,
    })
  }

  async function callDashboardComparisonAgent(agent, prompt, expectJson = true, referenceIr = null) {
    if (!mockModeEnabled) throw new Error('La comparación con agentes sólo está disponible en modo mock.')
    return aiCallDashboardImporterAgent(agent, prompt, expectJson, {
      token: sabreToken,
      apiBaseUrl: runtimeApiBaseUrl,
      apiKey: runtimeApiKey,
      timeoutMs: dashboardImporterTimeoutMs,
      signal: abortRef.current ? abortRef.current.signal : undefined,
      generationMode: 'import',
      referenceIr,
    })
  }

  async function simularScriptDashboard() {
    if (!mockModeEnabled) return
    if (ocupado || previewBusy) return
    setError('')
    setResultado(null)
    try {
      const scriptUuid =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now())
      const data = {
        ...JSON.parse(JSON.stringify(mockDashboardDataJson)),
        _uuid: scriptUuid,
        uuid: scriptUuid,
        title: 'TEST DASHBOARD MOCK ' + scriptUuid.slice(0, 8),
      }
      console.log('[dashboard mock] abriendo preview sin IA', data)
      await openDashboardPreview({
        title: data.title,
        data,
        ir: null,
        ficha: 'Mock generado desde Dashboard sin consumir IA.',
        origen: 'dashboard_mock',
      })
    } catch (e) {
      console.error('[dashboard mock] error', e)
      setError(e.message || String(e))
      setFase('error')
    }
  }

  async function createDashboardDraft({ title, data, ir, ficha, origen, catalogsJson, logicJson, prototype = false }) {
    const session = prototype
      ? {
          username: runtimeSession?.username || '',
          parent_pcc: runtimeSession?.parentPcc || runtimeSession?.parent_pcc || '',
          pcc: runtimeSession?.pcc || '',
        }
      : await resolveDashboardSession({ runtimeSession, runtimeToken: sabreToken, runtimeApiBaseUrl, runtimeApiKey })
    return {
      ...normalizeGeneratedScript({ title, data, ir, ficha, origen, session, catalogsJson, logicJson }),
      sourceFiles: files.map((file) => file.sourceFile).filter(Boolean),
    }
  }

  async function publishDashboardDraft(draft) {
    const options = {
      token: sabreToken,
      apiBaseUrl: runtimeApiBaseUrl,
      apiKey: runtimeApiKey,
    }
    await csLoadRuntimeTemplates(true)
    const payload = await buildDashboardDirectPublishPayload(draft, runtimeApiBaseUrl, runtimeApiKey, sabreToken)
    const validation = await dashboardValidatePublish(payload, options)
    if (validation?.ok === false) throw new Error(validation.message || 'No se pudo validar el paquete.')
    const response = await dashboardPublishRelease(payload, options)
    if (response?.ok === false) throw new Error(response.message || 'No se pudo publicar el script.')
    return response
  }

  function publishedResult(draft, response) {
    return {
      title: draft.title,
      nivel: null,
      mode: 'published',
      url: response.url,
      uuid: draft.uuid,
      type: draft.data?.type || draft.data?.typeof || draft.data?.type_of || '',
      pcc: draft.session?.pcc || '',
      username: draft.session?.username || '',
    }
  }

  async function openDashboardPreview({ title, data, ir, ficha, origen, catalogsJson, logicJson, prototype = false, prototypeStats = null }) {
    const draft = await createDashboardDraft({ title, data, ir, ficha, origen, catalogsJson, logicJson, prototype })
    const env = draft.data?.targetEnv || 'stg'
    const configJson = {
      config: {
        uuid: draft.uuid,
        useInternalConfig: true,
        enableRuntimeApi: !prototype,
        publicApiBaseUrl: runtimeApiBaseUrl || state.config?.publicApiBaseUrl || state.config?.apiBaseUrl || state.config?.bostonApi,
        apiBaseUrl: runtimeApiBaseUrl || state.config?.apiBaseUrl,
        apiKey: runtimeApiKey || state.config?.apiKey || state.config?.publicApiKey || state.config?.bostonAkey,
        env,
        advancedMode: advancedMode ? 1 : 0,
      },
    }
    // El runtime cambia durante el prototipo SST. Evitar que una plantilla previa
    // guardada en memoria vuelva a construir el iframe con comportamiento antiguo.
    await csLoadRuntimeTemplates(true)
    const html = await withDashboardRuntimeConfig(runtimeApiBaseUrl, runtimeApiKey, env, () =>
      csBuildSingleFile(draft.data, configJson, {
        previewMode: prototype,
        simulateApi: prototype,
        realPreview: !prototype,
        holdOnCompletion: true,
        token: prototype ? '' : sabreToken,
        env,
        title: draft.title,
        name: draft.name,
        uuid: draft.uuid,
        advancedMode,
        catalogsJson: draft.catalogsJson,
        logicJson: draft.logicJson,
      }),
    )
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
    setReviewFeedback('')
    setReviewConfirmOpen(false)
    setPreviewDraft({ ...draft, configJson, url, prototype, prototypeStats, prototypeRuntime: false })
    setFase('')
  }

  async function openConvertedDashboardSst(entry, { prototype = false, publish = false } = {}) {
    if (!entry) return
    try {
      const conversion = convertSstToBoston(normalizeReplacementChars(String(entry.content || '')), {
        ...buildSstConversionOptions(entry, files),
      })
      const adapted = conversion.boston
      if (!adapted?.dataJson || !Array.isArray(adapted.dataJson.forms) || !adapted.dataJson.forms.length) {
        throw new Error('El convertidor no produjo formularios compatibles con la vista Boston.')
      }
      const generatedScript = {
        title: adapted.dataJson.title || entry.name,
        data: adapted.dataJson,
        ir: conversion.ir,
        logicJson: adapted.logicJson,
        ficha: prototype
          ? 'Vista previa determinista SST. No utiliza agentes ni ejecuta comandos Sabre.'
          : 'Importación determinista SST. No utiliza Agente 1 ni Agente 3.',
        origen: prototype ? 'dashboard_sst_converter_preview' : 'dashboard_sst_converter_import',
        prototype,
        prototypeStats: {
          ...adapted.stats,
          logicBlocks: Object.keys(adapted.logicJson?.blocks || {}).length,
        },
      }
      if (publish && !prototype) {
        const draft = await createDashboardDraft(generatedScript)
        const response = await publishDashboardDraft(draft)
        setResultado(publishedResult(draft, response))
        if (typeof onPublished === 'function') onPublished(response)
        return { conversion, adapted, draft, response }
      }
      await openDashboardPreview(generatedScript)
      return { conversion, adapted, draft: null, response: null }
    } catch (e) {
      console.error('[dashboard sst converter]', e)
      setError(e.message || String(e))
      throw e
    }
  }

  async function buildComparisonSide(generatedScript, { mockReservation = null, realSabre = false } = {}) {
    const draft = await createDashboardDraft({ ...generatedScript, prototype: true })
    const configJson = {
      config: {
        uuid: draft.uuid,
        useInternalConfig: true,
        enableRuntimeApi: realSabre,
        publicApiBaseUrl: runtimeApiBaseUrl || state.config?.publicApiBaseUrl || state.config?.apiBaseUrl || state.config?.bostonApi,
        apiBaseUrl: runtimeApiBaseUrl || state.config?.apiBaseUrl,
        apiKey: runtimeApiKey || state.config?.apiKey || state.config?.publicApiKey || state.config?.bostonAkey,
        env: draft.data?.targetEnv || 'stg',
        advancedMode: realSabre ? 1 : (advancedMode ? 1 : 0),
      },
    }
    await csLoadRuntimeTemplates(true)
    let html = await withDashboardRuntimeConfig(runtimeApiBaseUrl, runtimeApiKey, configJson.config.env, () =>
      csBuildSingleFile(draft.data, configJson, {
        previewMode: !realSabre,
        simulateApi: !realSabre,
        realPreview: realSabre,
        holdOnCompletion: true,
        token: realSabre ? sabreToken : '',
        env: configJson.config.env,
        title: draft.title,
        name: draft.name,
        uuid: draft.uuid,
        advancedMode: realSabre || advancedMode,
        catalogsJson: draft.catalogsJson,
        logicJson: draft.logicJson,
      }),
    )
    if (!realSabre) {
      const safeMockReservation = JSON.stringify(mockReservation || comparisonMockReservation())
        .replace(/<\/script/gi, '<\\/script')
        .replace(/<!--/g, '<\\!--')
      const mockReservationShim = `<script>
(function(){
  var mockReservation = ${safeMockReservation};
  var originalCallAPI = window.callAPI;
  window.callAPI = async function(endpoint, method, body){
    if (String(endpoint || '').replace(/^\\/+/, '') === 'sabre/reservation') return mockReservation;
    return originalCallAPI(endpoint, method, body);
  };
  window.readReservation = function(){ return Promise.resolve(mockReservation); };
})();
<\/script>`
      html = html.replace(/<\/body>/i, mockReservationShim + '\n</body>')
    } else {
      const realSabreHarness = `<style>
#boston-real-test-harness{position:fixed;right:12px;bottom:12px;z-index:10050;width:min(390px,calc(100vw - 24px));padding:10px;border:2px solid #dc2626;border-radius:10px;background:#fff7f7;box-shadow:0 14px 38px rgba(15,23,42,.3);font:12px/1.35 system-ui;color:#7f1d1d}
#boston-real-test-harness strong{display:block;margin-bottom:6px}#boston-real-test-harness .rt-actions{display:flex;gap:6px;flex-wrap:wrap}
#boston-real-test-harness button{padding:6px 9px;border:0;border-radius:6px;font-weight:700;cursor:pointer}#boston-real-probe{background:#0369a1;color:#fff}#boston-real-save{background:#b91c1c;color:#fff}#boston-real-ignore{background:#e2e8f0;color:#334155}
#boston-real-meta,#boston-real-status{display:block;margin-top:6px;max-height:82px;overflow:auto;white-space:pre-wrap;color:#475569}#boston-real-meta{padding:5px;border-radius:5px;background:#fff;font:10px/1.35 Consolas,monospace}
</style><script>
(function(){
  var trace=[];
  var originalCallAPI=window.callAPI;
  function text(value){ try{return typeof value==='string'?value:JSON.stringify(value);}catch(_){return String(value||'');} }
  function statusNode(){return document.getElementById('boston-real-status');}
  function report(message){var node=statusNode();if(node)node.textContent=message;}
  window.callAPI=async function(endpoint,method,body){
    var started=Date.now();trace.push('→ '+(method||'GET')+' '+endpoint);report(trace.slice(-4).join('\\n'));
    try{
      var rsp=await originalCallAPI(endpoint,method,body);
      var flags=rsp&&typeof rsp==='object'?(rsp.pending?' [PENDING]':rsp.simulated?' [SIMULATED]':''):'';
      trace.push('← '+endpoint+' '+(Date.now()-started)+'ms'+flags);
      report(trace.slice(-4).join('\\n'));
      return rsp;
    }catch(err){trace.push('✕ '+endpoint+' '+(err&&err.message?err.message:String(err)));report(trace.slice(-4).join('\\n'));throw err;}
  };
  async function probe(){
    report('Consultando sabre/reservation…');
    try{
      var rsp=await window.readReservation();
      var locator=window.extractPnrLocator?window.extractPnrLocator(rsp):'';
      report(locator?'Conexión real confirmada. PNR: '+locator:'Respuesta sin PNR: '+text(rsp));
    }catch(err){report('Error de conexión: '+(err&&err.message?err.message:String(err)));}
  }
  window.addEventListener('DOMContentLoaded',function(){
    var box=document.createElement('div');box.id='boston-real-test-harness';
    var cfg=(window.__BOSTON_CONFIG__&&window.__BOSTON_CONFIG__.config)||{};
    box.innerHTML='<strong>⚠ Prueba conectada a Sabre real</strong><div class="rt-actions"><button id="boston-real-probe" type="button">Verificar conexión</button><button id="boston-real-save" type="button">Guardar cambios (ER)</button><button id="boston-real-ignore" type="button">Ignorar cambios (I)</button></div><span id="boston-real-meta"></span><span id="boston-real-status">Preparando prueba de conexión…</span>';
    document.body.appendChild(box);
    var status=box.querySelector('#boston-real-status');
    box.querySelector('#boston-real-meta').textContent='token='+(window.TOKEN?'presente':'FALTA')+' | api='+(cfg.enableRuntimeApi===true?'activa':'INACTIVA')+' | preview='+String(!!window.__BOSTON_PREVIEW_MODE__)+' | simulate='+String(!!window.__BOSTON_SIMULATE_API__)+'\\n'+(cfg.publicApiBaseUrl||cfg.apiBaseUrl||'URL API FALTANTE');
    box.querySelector('#boston-real-probe').onclick=probe;
    box.querySelector('#boston-real-save').onclick=async function(){
      if(!window.confirm('¿Guardar en Sabre los cambios pendientes del PNR activo con ER?'))return;
      status.textContent='Enviando ER…';
      try{var rsp=await window.executeCommand('ER');status.textContent='ER enviado. Respuesta: '+text(rsp);}
      catch(err){status.textContent='Error al enviar ER: '+(err&&err.message?err.message:String(err));}
    };
    box.querySelector('#boston-real-ignore').onclick=async function(){
      if(!window.confirm('¿Descartar los cambios pendientes del PNR activo con I?'))return;
      status.textContent='Enviando I…';
      try{var rsp=await window.executeCommand('I');status.textContent='I enviado. Respuesta: '+text(rsp);}
      catch(err){status.textContent='Error al enviar I: '+(err&&err.message?err.message:String(err));}
    };
    window.setTimeout(probe,250);
  });
})();
<\/script>`
      html = html.replace(/<\/body>/i, realSabreHarness + '\n</body>')
    }
    return {
      ...draft,
      url: URL.createObjectURL(new Blob([html], { type: 'text/html' })),
      metrics: comparisonMetrics(draft.data, draft.logicJson),
    }
  }

  function closeSstComparison() {
    setComparison((current) => {
      if (current?.engine?.url) URL.revokeObjectURL(current.engine.url)
      if (current?.agent?.url) URL.revokeObjectURL(current.agent.url)
      return null
    })
    setComparisonTab('preview')
    setComparisonRealConfirmOpen(false)
    setComparisonRealAccepted(false)
  }

  async function compareSstGeneration(entry) {
    if (!mockModeEnabled || !isDashboardSourceSst(entry) || processBusyRef.current) return
    setError('')
    setComparisonTab('preview')
    setComparison((current) => {
      if (current?.engine?.url) URL.revokeObjectURL(current.engine.url)
      if (current?.agent?.url) URL.revokeObjectURL(current.agent.url)
      return { open: true, busy: true, stage: 'Convirtiendo con el motor local…', error: '', sourceName: entry.name, engine: null, agent: null }
    })
    startAbortableProcess()
    try {
      const source = normalizeReplacementChars(String(entry.content || ''))
      const mockReservation = comparisonMockReservation()
      const conversion = convertSstToBoston(source, buildSstConversionOptions(entry, files))
      const adapted = conversion.boston
      if (!adapted?.dataJson || !dataJsonEjecutable(adapted.dataJson)) {
        throw new Error('El motor local no produjo un data.json ejecutable para comparar.')
      }
      const engine = await buildComparisonSide({
        title: adapted.dataJson.title || entry.name,
        data: adapted.dataJson,
        ir: conversion.ir,
        logicJson: adapted.logicJson,
        ficha: 'Comparador mock · motor determinístico local.',
        origen: 'dashboard_sst_comparison_engine',
      }, { mockReservation })
      setComparison((current) => ({
        ...current,
        engine,
        stage: 'Agente 1 está interpretando el mismo SST… Puede tardar varios minutos; no vuelvas a ejecutar.',
      }))

      const triageBlock = entry.triage ? triageToPromptBlock(entry.triage) + '\n\n' : ''
      const formsBlock = entry.formsDraft ? formsDraftToPromptBlock(entry.formsDraft) + '\n\n' : ''
      const promptIR =
        'MODO COMPARACIÓN MOCK. Procesá el siguiente SST y devolvé EXCLUSIVAMENTE el IR JSON según el contrato del system prompt. No uses el resultado del convertidor local: esta ejecución debe ser una muestra independiente del Agente 1.\n\n' +
        triageBlock + formsBlock + '=== ARCHIVO PRINCIPAL: ' + entry.name + ' ===\n' + source
      const r1 = await callDashboardComparisonAgent('agent1_sst', promptIR, true)
      if (!r1.ok || !r1.data?.data) throw new Error(r1.data?.error || 'El Agente 1 no devolvió un IR válido.')
      const agentIR = r1.data.data
      requireDashboardIRSummary(agentIR, '[dashboard comparison] IR del Agente 1')
      const interpretationEvaluation = evaluateAgentInterpretation(adapted.dataJson, adapted.logicJson, agentIR)
      setComparison((current) => ({ ...current, stage: 'Agente 3 está generando su data.json…' }))

      const promptData =
        'MODO COMPARACIÓN MOCK. Generá un Custom Script data.json usando únicamente el IR del Agente 1 incluido abajo. Devolvé exclusivamente el JSON según el contrato del system prompt. No uses ni imites el resultado del convertidor local.\n\n' +
        'IR DEL AGENTE 1:\n' + JSON.stringify(agentIR, null, 2)
      const r3 = await callDashboardComparisonAgent('agent3_gen', promptData, true, agentIR)
      if (!r3.ok || !r3.data?.data) throw new Error(r3.data?.error || 'El Agente 3 no devolvió un data.json válido.')
      let agentData = preserveStrictSstCommandPlan(r3.data.data, agentIR)
      try { agentData = normalizeDataJson(agentData).data } catch (_) { /* conservar respuesta original */ }
      if (!dataJsonEjecutable(agentData)) throw new Error('El resultado del agente no contiene formularios ni comandos ejecutables.')
      setComparison((current) => ({ ...current, stage: 'Agente 4 está generando su logic.json…' }))
      const promptLogic =
        'MODO COMPARACIÓN MOCK. Generá exclusivamente logic.json usando el IR y data.json incluidos abajo. Conservá fases, ramas y comandos del IR; no generes class App ni código JavaScript.\n\n' +
        'IR DEL AGENTE 1:\n' + JSON.stringify(agentIR, null, 2) +
        '\n\nDATA.JSON DEL AGENTE 3:\n' + JSON.stringify(agentData, null, 2)
      const r4 = await callDashboardComparisonAgent('agent4_scriptlogic', promptLogic, true, agentIR)
      if (!r4.ok || !r4.data?.data) throw new Error(r4.data?.error || 'El Agente 4 no devolvió un logic.json válido.')
      const agentLogic = r4.data.data
      const evaluation = evaluateAgentGeneration(adapted.dataJson, agentData, {
        referenceLogicJson: adapted.logicJson,
        agentLogicJson: agentLogic,
      })
      const agentSide = await buildComparisonSide({
        title: agentData.title || entry.name,
        data: agentData,
        ir: agentIR,
        logicJson: agentLogic,
        ficha: 'Comparador mock · resultado independiente de Agente 1 + Agente 3 + Agente 4.',
        origen: 'dashboard_sst_comparison_agent',
      }, { mockReservation })
      const agent = {
        ...agentSide,
        evaluation,
        interpretationEvaluation,
        runs: {
          agent1: { model: r1.model || '', usage: r1.usage || null },
          agent3: { model: r3.model || '', usage: r3.usage || null },
          agent4: { model: r4.model || '', usage: r4.usage || null },
        },
      }
      setComparison((current) => ({ ...current, busy: false, stage: '', agent, realSabre: false }))
    } catch (e) {
      if (esProcesoDetenido(e)) {
        setComparison((current) => current ? ({ ...current, busy: false, stage: '', error: 'Comparación detenida.' }) : current)
      } else {
        console.error('[dashboard sst comparison]', e)
        setComparison((current) => current ? ({ ...current, busy: false, stage: '', error: e.message || String(e) }) : current)
      }
    } finally {
      finishAbortableProcess()
    }
  }

  function downloadSstComparisonReport() {
    const current = comparison
    if (!current?.engine || !current?.agent || current.busy) return
    const report = {
      schemaVersion: 1,
      generatedAt: new Date().toISOString(),
      source: { fileName: current.sourceName || file?.name || 'script.sst' },
      engine: {
        metrics: current.engine.metrics || null,
        ir: current.engine.ir || null,
        dataJson: current.engine.data || null,
        logicJson: current.engine.logicJson || null,
      },
      agents: {
        runs: current.agent.runs || null,
        evaluation: current.agent.evaluation || null,
        interpretationEvaluation: current.agent.interpretationEvaluation || null,
        ir: current.agent.ir || null,
        dataJson: current.agent.data || null,
        logicJson: current.agent.logicJson || null,
      },
    }
    const baseName = String(current.sourceName || file?.name || 'script')
      .replace(/\.[^.]+$/, '')
      .replace(/[^A-Za-z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'script'
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${baseName}.comparison-report.json`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  async function setComparisonSabreMode(realSabre) {
    const current = comparison
    if (!current?.engine || !current?.agent || current.busy) return
    if (realSabre && !sabreToken) {
      setComparison((value) => ({ ...value, error: 'No hay token Sabre activo. Abre el Dashboard desde una sesión válida antes de iniciar la prueba real.' }))
      setComparisonRealConfirmOpen(false)
      return
    }
    setComparisonRealConfirmOpen(false)
    setComparison((value) => ({ ...value, busy: true, stage: realSabre ? 'Conectando ambas vistas con Sabre…' : 'Restaurando llamadas simuladas…', error: '' }))
    try {
      const mockReservation = realSabre ? null : comparisonMockReservation()
      const engine = await buildComparisonSide({
        title: current.engine.title,
        data: current.engine.data,
        ir: current.engine.ir,
        logicJson: current.engine.logicJson,
        ficha: current.engine.ficha,
        origen: current.engine.origen,
      }, { mockReservation, realSabre })
      const agent = await buildComparisonSide({
        title: current.agent.title,
        data: current.agent.data,
        ir: current.agent.ir,
        logicJson: current.agent.logicJson,
        ficha: current.agent.ficha,
        origen: current.agent.origen,
      }, { mockReservation, realSabre })
      if (current.engine.url) URL.revokeObjectURL(current.engine.url)
      if (current.agent.url) URL.revokeObjectURL(current.agent.url)
      setComparison((value) => ({
        ...value,
        busy: false,
        stage: '',
        engine,
        agent: {
          ...agent,
          evaluation: current.agent.evaluation,
          interpretationEvaluation: current.agent.interpretationEvaluation,
          runs: current.agent.runs,
        },
        realSabre,
      }))
    } catch (e) {
      console.error('[dashboard sst comparison sabre mode]', e)
      setComparison((value) => ({ ...value, busy: false, stage: '', error: e.message || String(e) }))
    }
  }

  async function previewDashboardSst(entry) {
    if (!mockModeEnabled) return
    if (!entry || sstPreviewBusy || ocupado) return
    setError('')
    setSstPreviewBusy(true)
    try {
      await openConvertedDashboardSst(entry, { prototype: true })
    } catch (e) {
      // openConvertedDashboardSst ya registra y muestra el error.
    } finally {
      setSstPreviewBusy(false)
    }
  }

  function closePreviewDraft() {
    const cur = previewDraft
    if (cur?.url) URL.revokeObjectURL(cur.url)
    if (cur?.uuid) setDrafts((list) => list.filter((d) => d.uuid !== cur.uuid))
    setPreviewDraft(null)
    setPreviewBusy('')
    setReviewFeedback('')
    setReviewConfirmOpen(false)
  }

  function togglePrototypeRuntime() {
    if (!mockModeEnabled) return
    setPreviewDraft((current) => current && current.prototype
      ? { ...current, prototypeRuntime: !current.prototypeRuntime }
      : current)
  }

  // Cerrar sin perder: el borrador queda en la lista de abajo para re-verlo y decidir despues.
  function stashCurrentDraft() {
    const cur = previewDraft
    if (cur) setDrafts((list) => (list.some((d) => d.uuid === cur.uuid) ? list : [cur, ...list]))
    setPreviewDraft(null)
    setPreviewBusy('')
    setReviewFeedback('')
    setReviewConfirmOpen(false)
  }

  function reopenDraft(draft) {
    setError('')
    setReviewFeedback('')
    setReviewConfirmOpen(false)
    setPreviewBusy('')
    setPreviewDraft(draft)
  }

  function discardDraft(draft) {
    if (draft?.url) URL.revokeObjectURL(draft.url)
    setDrafts((list) => list.filter((d) => d.uuid !== draft.uuid))
  }

  function handleSendToReview() {
    console.log('[dashboard modal] CLICK enviar a dev')
    if (!previewDraft || previewBusy) return
    setError('')
    setReviewConfirmOpen(true)
  }

  async function confirmSendToReview() {
    console.log('[dashboard modal] CONFIRM enviar a dev')
    if (!previewDraft || previewBusy) return
    const feedback = reviewFeedback.trim()
    if (!feedback) {
      setError('Escribe el motivo de envío a revisión.')
      return
    }
    setError('')
    setPreviewBusy('review')
    try {
      console.log('[dashboard modal] previewDraft', previewDraft)
      console.log('[dashboard modal] sabreToken', Boolean(sabreToken))
      console.log('[dashboard modal] send review clicked')
      const saved = await saveDashboardReview(previewDraft, {
        token: sabreToken,
        apiBaseUrl: runtimeApiBaseUrl,
        apiKey: runtimeApiKey,
      }, feedback)
      cacheDashboardReview(previewDraft, saved, feedback)
      setResultado({ title: previewDraft.title, nivel: null, mode: 'review', reviewId: saved.id })
      closePreviewDraft()
      if (typeof onReviewSent === 'function') onReviewSent()
    } catch (e) {
      console.error('[dashboard modal] enviar dev error', e)
      setError(e.message || String(e))
      setPreviewBusy('')
    }
  }

  async function handlePublishForMe() {
    console.log('[dashboard modal] CLICK publicar para mi')
    if (!previewDraft || previewBusy) return
    setError('')
    setPreviewBusy('publish')
    try {
      console.log('[dashboard modal] previewDraft', previewDraft)
      console.log('[dashboard modal] sabreToken', Boolean(sabreToken))
      console.log('[dashboard modal] publish me clicked')
      const response = await publishDashboardDraft(previewDraft)
      setResultado(publishedResult(previewDraft, response))
      closePreviewDraft()
      if (typeof onPublished === 'function') onPublished(response)
    } catch (e) {
      console.error('[dashboard modal] publicar para mi error', e)
      setError(e.message || String(e))
      setPreviewBusy('')
    }
  }

  function runTriageForEntry(entry, providedFiles) {
    if (!entry || entry.ssc || entry.role === 'dependency' || aiFileIsAuxiliary(entry.name)) {
      return { ...entry, triage: null, formsDraft: null }
    }
    let triage = null
    let formsDraft = null
    try {
      triage = triageSst(entry.content, entry.name, { providedFiles })
    } catch (e) {
      console.warn('[dash triage]', e)
    }
    try {
      const fd = buildFormsDraft(entry.content, entry.name)
      if (fd && fd.ok) formsDraft = fd
    } catch (e) {
      console.warn('[dash formsDraft]', e)
    }
    return { ...entry, triage, formsDraft }
  }

  function mergePickedEntries(current, entries) {
    const merged = [...current]
    const names = new Set(merged.map((c) => c.name.toUpperCase()))
    entries.forEach((entry) => {
      if (!names.has(entry.name.toUpperCase())) {
        merged.push(entry)
        names.add(entry.name.toUpperCase())
      }
    })
    const providedFiles = merged.map((entry) => entry.name)
    return merged.map((entry) => runTriageForEntry(entry, providedFiles))
  }

  function requestDependencyFiles(diagnostic = null) {
    setPendingDependencyUpload(diagnostic || { code: 'MULTIPLE_DEPENDENCIES' })
    if (fileRef.current) fileRef.current.click()
  }

  async function processPickedFile(f, options = {}) {
    const displayName = options.relativePath || f.name
    const buf = await f.arrayBuffer()
    const role = aiFileIsAuxiliary(displayName) ? 'dependency' : (options.dependencyUpload ? 'linked-script' : 'script')
    const fileRole = /\.(csv|xls|xlsx)$/i.test(displayName) ? 'catalog' : 'script_source'
    let sourceFile = null
    if (/\.(sst|ssc|csv|xls|xlsx)$/i.test(displayName)) {
      sourceFile = await dashboardUploadSourceFile(f, buf, {
        token: sabreToken,
        apiBaseUrl: runtimeApiBaseUrl,
        apiKey: runtimeApiKey,
        importId: options.importId || uuidV4(),
        uploadId: options.uploadId || uuidV4(),
        fileRole,
        targetEnv: state.config?.env || 'stg',
      })
    }
    let text = /\.(xls|xlsx)$/i.test(displayName)
      ? `[CATÁLOGO EXCEL BINARIO: ${displayName} · ${buf.byteLength} bytes · original conservado para descarga]`
      : new TextDecoder('utf-8', { fatal: false }).decode(buf)
    if (text.includes('�')) {
      try {
        text = new TextDecoder('windows-1252').decode(buf)
      } catch (e) {
        /* utf-8 queda */
      }
    }
    text = normalizeReplacementChars(text)
    let ssc = aiFileIsSSC(displayName)
    let decompiled = false
    if (ssc) {
      // Decompilador v2: el .ssc se reconstruye a pseudo-fuente y va por el camino fiel (Agente 1)
      const v2 = decompileSsc(buf)
      if (v2 && v2.ok && v2.sst) {
        text = v2.sst
        ssc = false
        decompiled = true
      }
    }
    return runTriageForEntry({
      name: displayName,
      relativePath: displayName,
      archiveName: options.archiveName || '',
      fromArchive: options.fromArchive === true,
      content: text,
      triage: null,
      ssc,
      decompiled,
      formsDraft: null,
      role,
      sourceFile,
    }, options.providedFiles || [displayName])
  }

  async function onPickFile(event) {
    const rawPicked = Array.from(event.target.files || [])
    event.target.value = ''
    if (!rawPicked.length) return
    let picked
    try {
      picked = await expandSstImportFiles(rawPicked)
    } catch (zipError) {
      setError('No se pudo abrir el ZIP: ' + (zipError.message || String(zipError)))
      return
    }
    if (!picked.length) {
      setError('El ZIP no contiene archivos SST, SSC, CSV, XLS, XLSX o TXT compatibles.')
      return
    }
    const dependencyUpload = Boolean(pendingDependencyUpload)
    const continuingImport = files.length > 0
    const importId = sourceImportIdRef.current || uuidV4()
    sourceImportIdRef.current = importId
    setError('')
    setResultado(null)
    setChat(null)
    setChatInput('')
    setFase('')
    setImportIR(null)
    setImportSummary('')
    setImportDescription('')
    if (!dependencyUpload) {
      if (previewDraft?.url) URL.revokeObjectURL(previewDraft.url)
      drafts.forEach((draft) => {
        if (draft?.url) URL.revokeObjectURL(draft.url)
      })
      setPreviewDraft(null)
      setPreviewBusy('')
      setReviewFeedback('')
      setReviewConfirmOpen(false)
      setDrafts([])
      clearPanelDraft(draftScope, draftUser)
    }
    const entries = []
    const providedFiles = picked.map((entry) => entry.relativePath)
    for (const pickedFile of picked) {
      try {
        entries.push(await processPickedFile(pickedFile.file, {
          dependencyUpload,
          importId,
          uploadId: uuidV4(),
          relativePath: pickedFile.relativePath,
          archiveName: pickedFile.archiveName,
          fromArchive: pickedFile.fromArchive,
          providedFiles,
        })) // eslint-disable-line no-await-in-loop
      } catch (uploadError) {
        setError('No se pudo conservar el archivo original «' + pickedFile.relativePath + '»: ' + (uploadError.message || String(uploadError)))
        return
      }
    }
    const pickedDiagnostics = entries
      .filter((entry) => entry.triage && Array.isArray(entry.triage.diagnostics))
      .flatMap((entry) => entry.triage.diagnostics)
    setPendingDependencyUpload(null)
    if (dependencyUpload || continuingImport) {
      setFiles((current) => mergePickedEntries(current, entries))
    } else {
      setFiles(entries)
    }
    if (dependencyUpload || pickedDiagnostics.some((diagnostic) => diagnostic && (diagnostic.severity === 'error' || diagnostic.severity === 'warning'))) {
      setDiagOpen(true)
    }
  }

function removePickedFile(name) {
    setImportIR(null)
    setImportSummary('')
    setImportDescription('')
    setFiles((current) => {
      const next = current.filter((c) => c.name !== name)
      if (!next.length) sourceImportIdRef.current = ''
      const providedFiles = next.map((entry) => entry.name)
      return next.map((entry) => runTriageForEntry(entry, providedFiles))
    })
  }

  // Dependencias CHAIN/SPAWN detectadas cuyo archivo aún NO fue subido → pedirlo.
  const allDeps = files.flatMap((f) => (f.triage && f.triage.dependenciasDetalle ? f.triage.dependenciasDetalle : []))
  const triageDiagnostics = files
    .filter((f) => f.triage && Array.isArray(f.triage.diagnostics))
    .flatMap((f) => f.triage.diagnostics)
  // Mejora 3: cobertura fuente → data.json del borrador generado
  let coverageDiagnostics = []
  try {
    const mainTriage = (files.find((f) => f.triage) || {}).triage
    const generated = (previewDraft && previewDraft.data) || null
    if (mainTriage && generated) coverageDiagnostics = checkCoverage(mainTriage, generated)
  } catch (e) { console.warn('[coverage]', e) }
  const allDiagnostics = [...triageDiagnostics, ...coverageDiagnostics]
  const loadedNames = files.map((f) => f.name)
  const isResolvedDependencyDiagnostic = (diagnostic) => {
    if (!diagnostic || (diagnostic.code !== 'MISSING_CSV_FILE' && diagnostic.code !== 'MISSING_SPAWN_TARGET')) return false
    const target = diagnostic.missingFile || diagnostic.file || diagnostic.target
    if (!target) return false
    return loadedNames.some((name) => fileMatchesDependency(name, target))
  }
  const visibleDiagnostics = allDiagnostics.filter((diagnostic) => !isResolvedDependencyDiagnostic(diagnostic))
  const parserMissingDeps = allDeps
    .filter((d) => !loadedNames.some((name) => fileMatchesDependency(name, d.destino)))
    .map((d) => d.destino)
  const diagnosticMissingDeps = visibleDiagnostics
    .filter((d) => d && (d.code === 'MISSING_CSV_FILE' || d.code === 'MISSING_SPAWN_TARGET'))
    .map((d) => d.missingFile || d.file || d.target)
    .filter(Boolean)
  const missingDeps = [...new Set([...parserMissingDeps, ...diagnosticMissingDeps])]
  const totalAlerts = files.reduce((sum, f) => sum + ((f.triage && f.triage.alertas) ? f.triage.alertas.length : 0), 0)
  const diagErrors = visibleDiagnostics.filter((diagnostic) => diagnostic && diagnostic.severity === 'error').length
  const diagWarnings = visibleDiagnostics.filter((diagnostic) => diagnostic && diagnostic.severity === 'warning').length

  function chatLastAgentText(current) {
    const messages = (current?.messages || []).filter((m) => m.role === 'agent')
    return messages.length ? messages[messages.length - 1].text : ''
  }

  function buildFullImportDescription(ir) {
    const meta = (ir && ir.meta) || {}
    const lines = []
    const title = meta.title || ir?.title || ir?.name || ''
    if (title) lines.push('SCRIPT: ' + title)
    lines.push(getRequiredIRSummary(ir))
    const reads = Array.isArray(meta.reads) ? meta.reads.filter(Boolean).join('; ') : String(meta.reads || '').trim()
    if (reads) {
      lines.push('')
      lines.push('LEE: ' + reads)
    }
    const writes = String(meta.writes || '').trim()
    if (writes) lines.push('ESCRIBE: ' + writes)
    const doesNot = String(meta.does_not || meta.doesNot || '').trim()
    if (doesNot) lines.push('NO HACE: ' + doesNot)
    const inputs = String(meta.inputs || '').trim()
    if (inputs) {
      lines.push('CAMPOS DEL FORM: ' + inputs)
    } else if (Array.isArray(ir?.fields) && ir.fields.length) {
      const fieldNames = ir.fields
        .filter((field) => field && field.visible !== false)
        .map((field) => field.label || field.name || field.tag || field.campoForm || field.id)
        .filter(Boolean)
      if (fieldNames.length) lines.push('CAMPOS DEL FORM: ' + fieldNames.slice(0, 20).join(', '))
    }
    const confidence = String(ir?.confidence || ir?.source?.confidence || 'medium').trim() || 'medium'
    lines.push('')
    lines.push('CONFIANZA: ' + confidence)
    const openQuestions = Array.isArray(ir?.open_questions) ? ir.open_questions : []
    const activeQuestions = openQuestions
      .map((question) => (typeof question === 'string' ? question : JSON.stringify(question)))
      .map((question) => String(question || '').trim())
      .filter(Boolean)
      .filter((question) => !/comentad[oa]s?|bloques?\s+comentad[oa]s?|^\s*;/.test(question.toLowerCase()))
    lines.push('')
    lines.push('DUDAS DEL INTÉRPRETE:')
    if (activeQuestions.length) activeQuestions.forEach((question) => lines.push('• ' + question))
    else lines.push('• Sin dudas bloqueantes detectadas.')
    return lines.join('\n')
  }

  function dashboardImportSignature(list = files) {
    return (list || [])
      .map((entry) => `${entry.role || 'script'}:${entry.name}:${String(entry.content || '').length}`)
      .sort()
      .join('|')
  }

  function handleDashboardDescriptionChange(event) {
    console.log('[dashboard-import] skip agent1 reason=description-only-change')
    setDescPreprocess(null)
    setDesc(event.target.value)
  }

  /* PASO A (archivo): SST usa el convertidor local; SSC conserva el intérprete remoto.
     Ambos caminos abren la misma revisión antes de construir el paquete. */
  async function interpretarArchivo() {
    if (!file || ocupado || processBusyRef.current) return
    setError('')
    setResultado(null)
    setChat(null)
    setChatInput('')
    const signature = dashboardImportSignature()
    if (importIR?.ir && importIR.signature === signature) {
      console.log('[dashboard-import] skip agent1 reason=cached-ir')
      setImportSummary(importIR.summary || '')
      setImportDescription((current) => String(current || '').trim() ? current : (importIR.fullDescription || buildFullImportDescription(importIR.ir)))
      setChat({
        source: 'file',
        ir: importIR.ir,
        fileName: importIR.fileName || files.map((f) => f.name).join(', '),
        catalogPreprocess: importIR.catalogPreprocess || null,
        messages: [{ role: 'agent', text: irToHumanText(importIR.ir) }],
      })
      setFase('')
      return
    }
    if (!signature) {
      console.log('[dashboard-import] skip agent1 reason=no-sst-source')
      return
    }
    startAbortableProcess()
    try {
      const esSstLocal = isDashboardSourceSst(file)
      setFase(esSstLocal ? 'sst' : 'agente1')
      if (esSstLocal) {
        console.log('[dashboard-import] skip agent1 reason=deterministic-sst', file.name)
        const conversion = convertSstToBoston(normalizeReplacementChars(String(file.content || '')), {
          ...buildSstConversionOptions(file, files),
        })
        const adapted = conversion.boston
        if (!adapted?.dataJson || !adapted?.logicJson?.entryBlock || !Object.keys(adapted.logicJson.blocks || {}).length) {
          throw new Error('El convertidor SST no produjo un IR local con data.json + logic.json ejecutables.')
        }
        const ir = buildDeterministicSstReviewIR(conversion, { fileName: file.name })
        const summary = requireDashboardIRSummary(ir, '[dashboard sst-local] IR determinista')
        const fullImportDescription = buildFullImportDescription(ir)
        const localImport = {
          signature,
          ir,
          summary,
          fullDescription: fullImportDescription,
          fileName: file.name,
          catalogPreprocess: null,
          conversionMode: 'sst_interpreter_v1',
        }
        setImportIR(localImport)
        setImportSummary(summary)
        setImportDescription(fullImportDescription)
        setChat({
          source: 'file',
          ir,
          fileName: file.name,
          catalogPreprocess: null,
          messages: [{ role: 'agent', text: irToHumanText(ir) }],
        })
        setFase('')
        return
      }
      console.log('[dashboard-import] run agent1 reason=explicit-import')
      const agent = file.ssc ? 'agent2_ssc' : 'agent1_sst'
      const multi = files.length > 1
      const catalogPreprocess = preprocessDashboardSstCatalogsForEntries(files)
      const bloques = files
        .map((f) => {
          const triageBlock = f.triage ? triageToPromptBlock(f.triage) + '\n\n' : ''
          const formsBlock = f.formsDraft ? formsDraftToPromptBlock(f.formsDraft) + '\n\n' : ''
          const label = f.role === 'dependency'
            ? '=== ARCHIVO ADJUNTO / DEPENDENCIA: '
            : f.role === 'linked-script'
              ? '=== SCRIPT DEPENDENCIA: '
              : '=== ARCHIVO PRINCIPAL: '
          const compactContent = catalogPreprocess.compactByName[f.name] || f.content
          return triageBlock + formsBlock + label + f.name + ' ===\n' + compactContent
        })
        .join('\n\n')
      const promptIR =
        `Procesá ${multi ? 'los siguientes archivos como UN ÚNICO flujo (el principal puede encadenar a los otros con CHAIN/SPAWN — integrá todo en un solo script)' : 'el siguiente archivo'} y devolvé EXCLUSIVAMENTE el IR JSON según el contrato del system prompt. NO agregues texto antes ni después salvo WARNINGS al final. Si el script muestra ALERTAS/confirmaciones al usuario, incluilas explícitamente en el IR y en la descripción.\n\n` +
        bloques +
        (catalogPreprocess.enabled ? catalogPreprocess.instruction : '')
      const r1 = await callDashboardFileAgent(agent, promptIR, true)
      if (!r1.ok || !r1.data || !r1.data.data) throw new Error((r1.data && r1.data.error) || 'El agente intérprete no devolvió IR válido')
      const ir = r1.data.data
      const summary = requireDashboardIRSummary(ir, '[dashboard agent1-debug] IR recibido')
      const fullImportDescription = buildFullImportDescription(ir)
      aiSetCostContext({ scriptName: files.map((f) => f.name).join(', ') })
      setImportIR({ signature, ir, summary, fullDescription: fullImportDescription, fileName: files.map((f) => f.name).join(', '), catalogPreprocess: catalogPreprocess.enabled ? catalogPreprocess : null })
      setImportSummary(summary)
      setImportDescription(fullImportDescription)
      setChat({ source: 'file', ir, fileName: files.map((f) => f.name).join(', '), catalogPreprocess: catalogPreprocess.enabled ? catalogPreprocess : null, messages: [{ role: 'agent', text: irToHumanText(ir) }] })
      setFase('')
    } catch (e) {
      if (esProcesoDetenido(e)) { setFase(''); return }
      setError(e.message)
      setFase('error')
    } finally {
      finishAbortableProcess()
    }
  }

  /* PASO A (descripción libre): la IA interpreta el pedido ANTES de generar — abre el chat. */
  async function interpretarDescripcion() {
    const texto = desc.trim()
    if (!texto || ocupado || processBusyRef.current) return
    persistPanelDraftNow()
    setError('')
    setResultado(null)
    setChat(null)
    setChatInput('')
    startAbortableProcess()
    try {
      setFase('agente1')
      const preprocess = preprocessDashboardDescriptionCatalogs(texto)
      setDescPreprocess(preprocess.enabled ? preprocess : null)
      const prompt =
        'MODO INTERPRETACIÓN — en esta respuesta NO generes JSON ni código.\n' +
        'El usuario pidió el siguiente script en lenguaje natural. Antes de construirlo, explicá en español y en texto plano cómo lo interpretás: objetivo del script, formularios y campos (tipo de dato, obligatoriedad), remarks/comandos Sabre que se enviarían, y los supuestos que estás tomando. Si hay ambigüedades, listalas al final como preguntas.\n\n' +
        reservationInterpretationGuide() + '\n\n' +
        'PEDIDO DEL USUARIO:\n' + (preprocess.enabled ? preprocess.text : texto) + (preprocess.instruction || '')
      const r = await callDashboardAgent('agent3_gen', prompt, false, 'natural')
      const textoInterp = String((r && r.data && r.data.data) || (r && r.raw) || '').trim()
      if (!r.ok || !textoInterp) throw new Error('El agente no devolvió una interpretación válida.')
      setChat({ source: 'desc', ir: null, fileName: '', catalogPreprocess: preprocess.enabled ? preprocess : null, messages: [{ role: 'agent', text: textoInterp }] })
      setFase('')
    } catch (e) {
      if (esProcesoDetenido(e)) { setFase(''); return }
      setError(e.message)
      setFase('error')
    } finally {
      finishAbortableProcess()
    }
  }

  /* Chat: el usuario corrige y el agente re-interpreta (tantas rondas como haga falta). */
  async function enviarCorreccion() {
    const correccion = chatInput.trim()
    if (!correccion || !chat || ocupado || processBusyRef.current) return
    setError('')
    const baseMessages = chat.source === 'file' && String(importDescription || '').trim()
      ? [{ role: 'agent', text: importDescription }]
      : chat.messages
    const nextMessages = [...baseMessages, { role: 'user', text: correccion }]
    setChat({ ...chat, messages: nextMessages })
    setChatInput('')
    if (chat.source === 'file' && isDashboardSourceSst(file)) {
      const note =
        'La observación quedó registrada, pero no modificará automáticamente el SST: el Agente 3 está deshabilitado para esta importación y el convertidor conservará la fuente original. Podrás aplicar el ajuste en Revisión Dev sobre data.json + logic.json.'
      setImportDescription([String(importDescription || '').trim(), 'Observación del usuario: ' + correccion].filter(Boolean).join('\n\n'))
      setChat({ ...chat, messages: [...nextMessages, { role: 'agent', text: note }] })
      return
    }
    startAbortableProcess()
    try {
      setFase('chat')
      const historial = nextMessages
        .map((m) => (m.role === 'agent' ? 'INTERPRETACIÓN DEL AGENTE:\n' : 'CORRECCIÓN DEL USUARIO:\n') + m.text)
        .join('\n\n---\n\n')
      const prompt =
        'MODO INTERPRETACIÓN — en esta respuesta NO generes JSON ni código.\n' +
        'Estás refinando con el usuario la interpretación de un script Sabre. Aplicá la ÚLTIMA corrección del usuario y devolvé la interpretación COMPLETA actualizada (no solo el cambio), en texto plano y con el mismo nivel de detalle. Si la corrección introduce una ambigüedad nueva, aclarala al final.\n\n' +
        historial +
        (chat.catalogPreprocess?.instruction
          ? '\n\nMantené estos catálogos como referencias compactas, sin expandir listas completas:\n' + chat.catalogPreprocess.instruction
          : '')
      const r = await callDashboardAgent('agent3_gen', prompt, false, 'edit')
      const textoNuevo = String((r && r.data && r.data.data) || (r && r.raw) || '').trim()
      if (!r.ok || !textoNuevo) throw new Error('El agente no pudo aplicar la corrección.')
      if (chat.source === 'file') setImportDescription(textoNuevo)
      setChat({ ...chat, messages: [...nextMessages, { role: 'agent', text: textoNuevo }] })
      setFase('')
    } catch (e) {
      if (esProcesoDetenido(e)) { setFase(''); return }
      setError(e.message)
      setFase('error')
    } finally {
      finishAbortableProcess()
    }
  }

  /* CONFIRMACIÓN: el texto interpretado va al constructor por lenguaje natural y se genera. */
  async function confirmarYGenerar() {
    if (!chat || ocupado || processBusyRef.current) return
    const textoConfirmado = chatLastAgentText(chat)
    if (!textoConfirmado) return
    setError('')
    // En SST, el IR cacheado se convierte localmente; los demás orígenes conservan su generador.
    startAbortableProcess()
    try {
      const esArchivo = chat.source === 'file'
      const esSstDeterminista = esArchivo && isDashboardSourceSst(file)
      setFase(esSstDeterminista ? 'sst' : 'agente3')
      const cachedIR = esArchivo ? (chat.ir || importIR?.ir) : null
      const activeCatalogPreprocess = esArchivo
        ? (chat.catalogPreprocess || importIR?.catalogPreprocess || null)
        : (chat.catalogPreprocess || descPreprocess || preprocessDashboardDescriptionCatalogs(desc))
      if (esArchivo && !cachedIR) {
        console.log('[dashboard-import] skip agent3 reason=missing-cached-ir')
        throw new Error('Primero importa y valida el SST para obtener el IR antes de generar data.json.')
      }

      if (esSstDeterminista) {
        console.log('[dashboard-import] skip agent3 reason=deterministic-sst', file.name)
        await openConvertedDashboardSst(file, { prototype: false, publish: true })
        setChat(null)
        setChatInput('')
        setFase('')
        return
      }
      const visibleDescription = esArchivo ? String(importDescription || importSummary || importIR?.summary || '').trim() : String(desc || '').trim()
      const formsBlock = esArchivo && files.length
        ? files.filter((f) => f.formsDraft).map((f) => '\n\n' + formsDraftToPromptBlock(f.formsDraft)).join('')
        : ''
      const irBlock = esArchivo && cachedIR ? '\n\nIR DE REFERENCIA (del intérprete cacheado; NO volver a ejecutar Agente 1):\n' + JSON.stringify(cachedIR, null, 2) : ''
      const prompt =
        (esArchivo
          ? 'Generá un Custom Script data.json usando el IR cacheado de Agente 1. NO vuelvas a interpretar el SST ni solicites Agente 1. La descripción visible es metadata editable del usuario; si difiere del IR, no invalida el IR.\n\nDESCRIPCIÓN VISIBLE:\n'
          : 'Generá un Custom Script data.json a partir de esta descripción CONFIRMADA por el usuario:\n\n' + reservationGenerationContract() + '\n\n') +
        (esArchivo ? (visibleDescription || textoConfirmado) : textoConfirmado) +
        (activeCatalogPreprocess?.instruction ? activeCatalogPreprocess.instruction : '') +
        formsBlock + irBlock
      const transactionIntentText = [
        desc,
        visibleDescription,
        textoConfirmado,
        ...(Array.isArray(chat.messages) ? chat.messages.map((message) => message && message.text) : []),
      ].filter(Boolean).join('\n\n')
      const applyDashboardTransactionIntent = (currentData) => {
        const applied = applyTransactionIntent(currentData, transactionIntentText)
        if (applied.changes.length) {
          console.warn('[dashboard transaction intent] ajustes aplicados:', applied.changes)
        }
        return applied.data
      }
      if (esArchivo && cachedIR) {
        requireDashboardIRSummary(cachedIR, '[dashboard agent3-debug] IR enviado a Agente 3')
      }
      console.log('[dashboard-import] run agent3 using cached IR', Boolean(esArchivo && cachedIR))
      const r3 = esArchivo
        ? await callDashboardFileAgent('agent3_gen', prompt, true, 'import', cachedIR)
        : await callDashboardAgent('agent3_gen', prompt, true)
      if (!r3.ok || !r3.data || !r3.data.data) throw new Error((r3.data && r3.data.error) || 'El agente generador no devolvió data.json válido')
      let data = preserveStrictSstCommandPlan(r3.data.data, cachedIR)
      if (activeCatalogPreprocess?.enabled) {
        data = applyDashboardDescriptionCatalogRefs(data, activeCatalogPreprocess)
      }
      data = applyDashboardTransactionIntent(data)
      if (!dataJsonEjecutable(data)) {
        throw new Error('El generador devolvió un data.json sin formularios NI comandos ejecutables (respuesta parcial). Reintentá; si el script es grande, generálo desde el admin (IA·Importar) que genera por partes.')
      }
      // NORMALIZADOR DETERMINISTA: repara estructuras rotas del agente (emisores
      // ocultos con campoForm 'a,b,c', block 99, masks de espacios, skip incoherente)
      // ANTES de cualquier lint. Es lo que convierte el data.json en ejecutable.
      try {
        const normalized = normalizeDataJson(data)
        if (normalized.changes.length) {
          console.warn('[dashboard normalize] ' + normalized.changes.length + ' reparaciones:', normalized.changes)
          data = normalized.data
        }
        data = applyDashboardTransactionIntent(data)
        if (activeCatalogPreprocess?.enabled) {
          data = applyDashboardDescriptionCatalogRefs(data, activeCatalogPreprocess)
        }
      } catch (normErr) { console.warn('[dashboard normalize]', normErr) }
      // LINT DETERMINISTA + AUTO-CORRECCIÓN: si algún placeholder no matchea o un
      // campo del form nunca se emite, se le exige al agente 3 la corrección ANTES
      // de mostrar el preview. Así el script no sale con inputs que no llegan a Sabre.
      let lintReport = lintDataJson(data)
      if (lintErrors(lintReport).length) {
        console.warn('[dashboard lint] errores deterministas, auto-corrigiendo:', lintErrors(lintReport).map((d) => d.code))
        try {
          const rFix = esArchivo
            ? await callDashboardFileAgent('agent3_gen', lintFixPrompt(lintReport, data) + irBlock + (activeCatalogPreprocess?.instruction || ''), true, 'repair', cachedIR)
            : await callDashboardAgent('agent3_gen', lintFixPrompt(lintReport, data) + (activeCatalogPreprocess?.instruction || ''), true, 'repair')
          if (rFix.ok && rFix.data && rFix.data.data) {
            let fixed = preserveStrictSstCommandPlan(rFix.data.data, cachedIR)
            try { fixed = normalizeDataJson(fixed).data } catch (_) {}
            if (activeCatalogPreprocess?.enabled) {
              fixed = applyDashboardDescriptionCatalogRefs(fixed, activeCatalogPreprocess)
            }
            fixed = applyDashboardTransactionIntent(fixed)
            const repairLosses = validateRepairPreservation(data, fixed)
            if (repairLosses.length) {
              console.warn('[dashboard lint] corrección descartada por pérdida funcional:', repairLosses)
            }
            if (dataJsonEjecutable(fixed) && !repairLosses.length && lintErrors(lintDataJson(fixed)).length < lintErrors(lintReport).length) {
              data = fixed
              lintReport = lintDataJson(data)
              console.log('[dashboard lint] corrección aplicada, errores restantes:', lintErrors(lintReport).length)
            }
          }
        } catch (fixErr) {
          console.warn('[dashboard lint] auto-corrección falló, se continúa con el original:', fixErr && fixErr.message)
        }
      }
      const finalLintErrors = lintErrors(lintReport)
      if (finalLintErrors.length) {
        throw new Error(
          'El resultado conserva errores estructurales después del intento de reparación: ' +
          finalLintErrors.map((diagnostic) => diagnostic.message).join(' '),
        )
      }
      let reservationDiagnostics = esArchivo ? [] : validateReservationRequirements(data, transactionIntentText)
      let reservationErrors = reservationDiagnostics.filter((diagnostic) => diagnostic.severity === 'error')
      if (!esArchivo && reservationErrors.length) {
        console.warn('[dashboard reservation] lecturas incompletas, auto-corrigiendo:', reservationErrors.map((item) => item.code))
        try {
          const rFix = await callDashboardAgent(
            'agent3_gen',
            reservationFixPrompt(reservationDiagnostics, data, transactionIntentText),
            true,
            'repair',
          )
          if (rFix.ok && rFix.data?.data) {
            let fixed = rFix.data.data
            try { fixed = normalizeDataJson(fixed).data } catch (_) {}
            if (activeCatalogPreprocess?.enabled) fixed = applyDashboardDescriptionCatalogRefs(fixed, activeCatalogPreprocess)
            fixed = applyDashboardTransactionIntent(fixed)
            const repairLosses = validateRepairPreservation(data, fixed)
            const repairedLint = lintDataJson(fixed)
            const repairedReservationDiagnostics = validateReservationRequirements(fixed, transactionIntentText)
            const repairedReservationErrors = repairedReservationDiagnostics.filter((item) => item.severity === 'error')
            if (
              dataJsonEjecutable(fixed) &&
              !repairLosses.length &&
              !lintErrors(repairedLint).length &&
              repairedReservationErrors.length < reservationErrors.length
            ) {
              data = fixed
              lintReport = repairedLint
              reservationDiagnostics = repairedReservationDiagnostics
              reservationErrors = repairedReservationErrors
              console.log('[dashboard reservation] corrección aplicada, errores restantes:', reservationErrors.length)
            } else if (repairLosses.length) {
              console.warn('[dashboard reservation] corrección descartada por pérdida funcional:', repairLosses)
            }
          }
        } catch (fixErr) {
          console.warn('[dashboard reservation] auto-corrección falló:', fixErr && fixErr.message)
        }
      }
      if (reservationErrors.length) {
        throw new Error(
          'El resultado no cumple los datos de reserva solicitados: ' +
          reservationErrors.map((diagnostic) => diagnostic.message).join(' '),
        )
      }
      const name = scriptName(String(data.title || (esArchivo && file && file.name) || 'script_ia').replace(/\.(sst|ssc)$/i, ''))
      let ficha = null
      try {
        ficha = buildFichaMd({
          triage: esArchivo && file ? file.triage : null,
          ir: cachedIR,
          dataJson: data,
          name,
          uuid: uuidV4(),
          warnings: (r3.data.warnings || []).concat(
            (lintReport || []).filter((d) => d.severity !== 'info').map((d) => '[lint ' + d.code + '] ' + d.message),
            reservationDiagnostics.filter((d) => d.severity !== 'info').map((d) => '[reservation ' + d.code + '] ' + d.message),
          ),
        })
      } catch (e) {
        /* opcional */
      }
      const generatedScript = {
        title: data.title || name,
        data,
        ir: cachedIR,
        ficha,
        origen: esArchivo
          ? 'Generado desde el dashboard público (archivo interpretado y confirmado' + (file ? ' ' + file.name : '') + ')'
          : 'Generado desde el dashboard público (descripción confirmada): ' + textoConfirmado.slice(0, 200),
        runtimeSession,
        runtimeToken,
        runtimeApiBaseUrl,
        runtimeApiKey,
        catalogsJson: activeCatalogPreprocess?.enabled ? activeCatalogPreprocess.catalogsJson : null,
      }
      const draft = await createDashboardDraft(generatedScript)
      const response = await publishDashboardDraft(draft)
      setResultado(publishedResult(draft, response))
      if (!esArchivo) clearGeneratedDescriptionDraft()
      if (typeof onPublished === 'function') onPublished(response)
      setChat(null)
      setChatInput('')
      setFase('')
    } catch (e) {
      if (esProcesoDetenido(e)) { setFase(''); return }
      setError(e.message)
      setFase('error')
    } finally {
      finishAbortableProcess()
    }
  }

  const ocupado = fase === 'agente1' || fase === 'agente3' || fase === 'sst' || fase === 'chat'
  const generatorLoadingText =
    fase === 'agente1'
      ? tr('loaderInterpreting')
      : fase === 'chat'
        ? tr('loaderApplying')
        : fase === 'sst'
          ? 'Convirtiendo SST a data.json + logic.json sin agentes IA…'
          : fase === 'agente3'
          ? tr('loaderGenerating')
          : ''

  return (
    <aside className="ia-panel" aria-busy={ocupado ? 'true' : 'false'}>
      <DiagnosticsModal
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        diagnostics={visibleDiagnostics}
        title="Validaciones del archivo importado"
        onRequestFiles={requestDependencyFiles}
      />
      <span className="ia-chip">
        <IASpark /> IA
      </span>
      <h2>{tr('panelTitle')}</h2>
      <p className="ia-sub">{tr('panelSub')}</p>

      <div className="ia-step">
        <span className="ia-step-num">1</span>
        <strong>{tr('step1')}</strong>
      </div>
      <p className="ia-hint">{tr('step1Hint')}</p>
      <input ref={fileRef} type="file" accept=".zip,.sst,.ssc,.txt,.csv,.xls,.xlsx,.json" multiple style={{ display: 'none' }} onChange={onPickFile} />
      <div className="ia-file-row">
        <button type="button" className="ia-btn ghost" onClick={() => { setPendingDependencyUpload(null); if (fileRef.current) fileRef.current.click() }} disabled={ocupado}>
          {tr('chooseFile')}
        </button>
        {!files.length ? <span className="ia-file-name">{tr('noFile')}</span> : null}
      </div>
      {files.length ? (
        <div className="ia-files-list">
          {files.map((f) => (
            <div className="ia-file-item" key={f.name}>
              <span className="ia-file-name" title={f.name}>
                {f.name}
                {f.decompiled ? ' · 🔬✔' : ''}
                {f.role === 'dependency' ? ' · adjunto' : f.role === 'linked-script' ? ' · dependencia' : ''}
                {f.triage && f.triage.alertas && f.triage.alertas.length ? ` · ⚠${f.triage.alertas.length}` : ''}
              </span>
              <button type="button" className="ia-btn ghost mini" onClick={() => removePickedFile(f.name)} disabled={ocupado} title={tr('removeFile')}>
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {missingDeps.length ? (
        <div className="ia-deps-warning">
          🔗 {tr('depsWarning', { deps: missingDeps.map((d) => `«${d}»`).join(', ') })}
          <button type="button" className="ia-btn ghost mini" onClick={() => requestDependencyFiles(null)} disabled={ocupado}>
            {tr('chooseFile')}
          </button>
        </div>
      ) : null}
      {file && (file.triage || file.formsDraft) ? (
        <div className="ia-triage" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {file.triage ? (
            <span className={'ia-risk ' + (/R[12]/.test(file.triage.nivel) ? 'ok' : /R3/.test(file.triage.nivel) ? 'warn' : 'err')}>
              {file.triage.nivel.split(' · ')[0]}
            </span>
          ) : null}
          <span className="ia-triage-txt">
            {file.triage ? `${file.triage.stats.comandos} cmd · ${file.triage.stats.defines} vars` : ''}
            {file.formsDraft ? ` · 🧩 ${file.formsDraft.stats.forms} form(s)/${file.formsDraft.stats.campos}` : ''}
          </span>
          {totalAlerts ? (
            <span className="ia-alert-chip" title={files.flatMap((f) => (f.triage && f.triage.alertas ? f.triage.alertas : [])).map((a) => `• ${a.titulo || ''}: ${a.mensaje}`).join('\n')}>
              ⚠ {tr('alertsChip', { n: totalAlerts })}
            </span>
          ) : null}
          {allDiagnostics.length ? (
            <button
              type="button"
              className="ia-alert-chip"
              onClick={() => setDiagOpen(true)}
              title="Ver validaciones deterministas del archivo"
            >
              {diagErrors ? '⛔' : '⚠️'} {allDiagnostics.length} validacion{allDiagnostics.length === 1 ? '' : 'es'}
              {diagErrors ? ` · ${diagErrors} error${diagErrors === 1 ? '' : 'es'}` : diagWarnings ? ` · ${diagWarnings} warn` : ''}
            </button>
          ) : null}
          <button type="button" className="ia-btn ghost mini" onClick={() => setShowAnalysis(true)}>{tr('viewAnalysis')}</button>
        </div>
      ) : null}
      {showAnalysis && file && (file.triage || file.formsDraft) ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowAnalysis(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
        >
          <div onClick={(event) => event.stopPropagation()} style={{ width: 'min(900px, 96vw)', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', background: '#fff', borderRadius: 10, boxShadow: '0 24px 64px rgba(15,23,42,.35)', padding: 18, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <strong>{files.map((f) => f.name).join(' + ')}</strong>
              <button type="button" className="ia-btn ghost mini" onClick={() => setShowAnalysis(false)}>{tr('close')}</button>
            </div>
            {files.map((f) => (
              <div key={f.name} style={{ marginBottom: 12 }}>
                {files.length > 1 ? <div style={{ fontWeight: 700, fontSize: 12.5, margin: '4px 0' }}>{f.name}</div> : null}
                {f.triage ? <TriageCard t={f.triage} open compact /> : null}
                {f.formsDraft ? <FormsDraftCard d={f.formsDraft} open compact /> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {mockModeEnabled && file && /\.sst$/i.test(String(file.name || '')) && !file.ssc ? (
        <div className="sst-mock-actions">
          <button
            type="button"
            className="ia-btn ghost"
            onClick={() => previewDashboardSst(file)}
            disabled={ocupado || sstPreviewBusy || Boolean(previewBusy) || Boolean(comparison?.busy)}
            title="Convierte el SST localmente a data.json + logic.json, sin agentes ni consumo de tokens"
          >
            {sstPreviewBusy ? 'Convirtiendo SST…' : 'Vista previa SST sin IA'}
          </button>
          <button
            type="button"
            className="ia-btn primary soft"
            onClick={() => compareSstGeneration(file)}
            disabled={ocupado || sstPreviewBusy || Boolean(previewBusy) || Boolean(comparison?.busy)}
            title="Compara el motor local contra una ejecución independiente de Agente 1 + Agente 3"
          >
            Comparar motor vs agente
          </button>
        </div>
      ) : null}
      <button type="button" className="ia-btn primary" onClick={interpretarArchivo} disabled={!file || ocupado}>
        {fase === 'agente1' ? tr('interpreting') : fase === 'sst' ? 'Convirtiendo SST…' : tr('interpretScript')}
      </button>
      {ocupado && generatorLoadingText ? (
        <div className="ia-procrow">
          <DashboardLoader text={generatorLoadingText} />
          <button type="button" className="ia-btn ghost mini ia-stop" onClick={detenerProceso} title={tr('stopTitle')}>
            {tr('stop')}
          </button>
        </div>
      ) : null}

      <div className="ia-step">
        <span className="ia-step-num">2</span>
        <strong>{tr('step2')}</strong>
      </div>
      <p className="ia-hint">{tr('step2Hint')}</p>
      <textarea
        className="ia-textarea"
        rows={3}
        placeholder={tr('descPlaceholder')}
        value={desc}
        onChange={handleDashboardDescriptionChange}
        onBlur={persistPanelDraftNow}
        disabled={ocupado}
      />
      <button type="button" className="ia-btn primary soft" onClick={interpretarDescripcion} disabled={desc.trim() === '' || ocupado}>
        {fase === 'agente1' && !file ? `⏳ ${tr('interpreting')}` : tr('interpretAI')}
      </button>

      {chat ? (
        <div className="ia-chat">
          <div className="ia-chat-head">
            <h4>{tr('chatTitle')}</h4>
            <span className="ia-chat-src">
              {chat.source === 'file' ? tr('chatFile', { name: chat.fileName || '' }) : tr('chatDesc')}
            </span>
          </div>
          {chat.source === 'file' ? (
            <div style={{ margin: '0 0 10px' }}>
              <strong>Así lo interpretó el agente:</strong>{' '}
              <textarea
                className="ia-textarea"
                rows={9}
                value={importDescription}
                onChange={(event) => {
                  console.log('[dashboard-import] skip agent1 reason=import-description-edit')
                  setImportDescription(event.target.value)
                }}
                placeholder="Revisa o ajusta la interpretacion del agente..."
                disabled={ocupado}
              />
            </div>
          ) : null}
          {chat.source === 'file' && file && file.ssc ? (
            <p className="ia-hint" style={{ color: '#b07b1a', margin: 0 }}>{tr('sscPartial')}</p>
          ) : null}
          {(chat.source !== 'file' || fase === 'chat') ? (
          <div className="ia-chat-messages">
            {(chat.source !== 'file' ? chat.messages : []).map((m, i) => (
              <div key={i} className={`ia-chat-msg ${m.role}`}>
                <span className="ia-chat-who">{m.role === 'agent' ? tr('agent') : tr('you')}</span>
                <p>{m.text}</p>
              </div>
            ))}
            {fase === 'chat' ? (
              <div className="ia-chat-msg agent pending">
                <span className="ia-chat-who">{tr('agent')}</span>
                <p>{tr('applyingFix')}</p>
              </div>
            ) : null}
          </div>
          ) : null}
          <div className="ia-chat-inputrow">
            <textarea
              className="ia-textarea"
              rows={2}
              placeholder={tr('chatPlaceholder')}
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              disabled={ocupado}
            />
            <button type="button" className="ia-btn ghost" onClick={enviarCorreccion} disabled={ocupado || chatInput.trim() === ''}>
              {tr('sendFix')}
            </button>
          </div>
          <div className="ia-chat-actions">
            <button type="button" className="ia-btn primary" onClick={confirmarYGenerar} disabled={ocupado}>
              {fase === 'agente3' ? tr('generating') : tr('confirmGenerate')}
            </button>
            {ocupado ? (
              <button type="button" className="ia-btn ghost mini ia-stop" onClick={detenerProceso} title={tr('stopTitle')}>
                {tr('stop')}
              </button>
            ) : null}
            <button type="button" className="ia-btn ghost mini" onClick={() => { setChat(null); setChatInput('') }} disabled={ocupado}>
              {tr('discard')}
            </button>
          </div>
        </div>
      ) : null}
      {mockModeEnabled ? (
        <button type="button" className="ia-btn ghost" onClick={simularScriptDashboard} disabled={ocupado || Boolean(previewBusy)}>
          Simular script
        </button>
      ) : null}

      {resultado ? (
        <div className="ia-result ok">
          ✅ <strong>{humanizeScriptName(resultado.title)}</strong>{' '}
          {resultado.mode === 'published' ? (
            <>
              {tr('published')}
              {resultado.url ? (
                <>
                  {' · '}
                  <a
                    href={resultado.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => {
                      if (typeof onOpenPublished !== 'function') return
                      event.preventDefault()
                      onOpenPublished(resultado)
                    }}
                  >
                    {tr('open')}
                  </a>
                </>
              ) : null}
            </>
          ) : (
            <>{tr('inReview')}</>
          )}
          {resultado.nivel ? <span className="ia-triage-txt"> · {resultado.nivel.split(' · ')[0]}</span> : null}
          <button
            type="button"
            className="ia-btn ghost mini"
            onClick={() => {
              setResultado(null)
              setFiles([])
              sourceImportIdRef.current = ''
              setDesc('')
              setImportIR(null)
              setImportSummary('')
              setImportDescription('')
              setFase('')
            }}
          >
            {tr('generateAnother')}
          </button>
        </div>
      ) : null}
      {error ? <div className="ia-result err">⚠ {error}</div> : null}

      {drafts.length ? (
        <div className="ia-drafts" style={{ marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
            {tr('draftsTitle')} · {drafts.length}
          </div>
          {drafts.map((d) => (
            <div key={d.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>borrador · {d.session?.pcc || ''} {d.session?.username || ''}</div>
              </div>
              <button type="button" className="ia-btn ghost mini" onClick={() => reopenDraft(d)} disabled={Boolean(previewBusy)}>{tr('viewPreview')}</button>
              <button type="button" className="ia-btn ghost mini" onClick={() => discardDraft(d)} disabled={Boolean(previewBusy)} title={tr('discard')}>✕</button>
            </div>
          ))}
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{tr('draftHint')}</div>
        </div>
      ) : null}

      {comparison ? (
        <div className="sst-comparison-overlay" role="dialog" aria-modal="true" aria-labelledby="sst-comparison-title">
          <div className="sst-comparison-modal">
            <div className="sst-comparison-head">
              <div>
                <div className={`sst-comparison-kicker ${comparison.realSabre ? 'live' : ''}`}>
                  {comparison.realSabre ? 'Prueba real conectada a Sabre' : 'Simulador mock · mismo archivo de entrada'}
                </div>
                <h3 id="sst-comparison-title">Motor local vs agentes IA</h3>
                <p>
                  {file?.name || 'SST'} · no publica scripts
                  {comparison.realSabre ? ', pero los comandos ejecutados sí afectan el PNR activo.' : ' ni modifica reservaciones.'}
                </p>
              </div>
              <button type="button" className="ia-btn ghost mini" onClick={closeSstComparison} disabled={comparison.busy}>Cerrar</button>
            </div>
            <div className="sst-comparison-tabs" role="tablist" aria-label="Vista del comparador">
              {[
                ['preview', 'Vista ejecutable'],
                ['data', 'data.json'],
                ['logic', 'logic.json'],
              ].map(([key, label]) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={comparisonTab === key}
                  className={comparisonTab === key ? 'active' : ''}
                  onClick={() => setComparisonTab(key)}
                  key={key}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="sst-comparison-grid">
              {[
                ['engine', 'Nuestro motor', comparison.realSabre ? 'Sabre real · PNR activo' : 'Determinístico · Sabre simulado', comparison.engine],
                ['agent', 'Resultado del agente', comparison.realSabre ? 'Sabre real · PNR activo' : 'Agentes 1 + 3 + 4 · Sabre simulado', comparison.agent],
              ].map(([key, title, subtitle, side]) => (
                <section className={`sst-comparison-side ${key}`} key={key}>
                  <header>
                    <div>
                      <strong>{title}</strong>
                      <span>{subtitle}</span>
                    </div>
                    {side?.metrics ? (
                      <div className="sst-comparison-metrics">
                        {side.evaluation ? (
                          <span className={side.evaluation.pass ? 'good' : 'bad'}><b>{side.evaluation.score}</b>/100</span>
                        ) : null}
                        {side.interpretationEvaluation ? (
                          <span className={side.interpretationEvaluation.pass ? 'good' : 'bad'}><b>A1 {side.interpretationEvaluation.score}</b>/100</span>
                        ) : null}
                        <span><b>{side.metrics.forms}</b> forms</span>
                        <span><b>{side.metrics.fields}</b> campos</span>
                        <span><b>{side.metrics.commands}</b> comandos</span>
                        <span><b>{side.metrics.logicBlocks}</b> bloques</span>
                        <span><b>{side.metrics.warnings}</b> avisos</span>
                        <span className={side.metrics.errors ? 'bad' : 'good'}><b>{side.metrics.errors}</b> errores</span>
                      </div>
                    ) : null}
                  </header>
                  <div className="sst-comparison-content">
                    {side?.evaluation ? (
                      <div className={`sst-comparison-evaluation ${side.evaluation.pass ? 'good' : 'bad'}`}>
                        <strong>{side.evaluation.pass ? 'Cobertura funcional aprobada' : 'Pérdidas funcionales detectadas'}</strong>
                        <span>
                          A1 {side.interpretationEvaluation?.score ?? '—'}/100 · final {side.evaluation.score}/100 ·{' '}
                          Campos {Math.round(side.evaluation.metrics.fieldRecall * 100)}% · comandos {Math.round(side.evaluation.metrics.commandRecall * 100)}% ·
                          catálogos {Math.round(side.evaluation.metrics.catalogRecall * 100)}% · navegación {Math.round(side.evaluation.metrics.navigationRecall * 100)}%
                        </span>
                        {side.evaluation.issues.length ? (
                          <ul>
                            {side.evaluation.issues.slice(0, 5).map((issue, index) => (
                              <li key={`${issue.code}-${index}`}>{issue.message}</li>
                            ))}
                            {side.evaluation.issues.length > 5 ? <li>Y {side.evaluation.issues.length - 5} diferencias más.</li> : null}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                    {!side ? (
                      <div className="sst-comparison-wait">
                        {comparison.busy ? <DashboardLoader text={key === 'agent' ? comparison.stage : 'Preparando resultado…'} /> : 'Sin resultado'}
                      </div>
                    ) : comparisonTab === 'preview' ? (
                      <iframe
                        title={`Vista ${title}`}
                        src={side.url}
                        sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
                      />
                    ) : (
                      <pre>{comparisonJson(comparisonTab === 'data' ? side.data : side.logicJson)}</pre>
                    )}
                  </div>
                </section>
              ))}
            </div>
            <div className={`sst-comparison-foot ${comparison.realSabre ? 'live' : ''}`}>
              <span>
                {comparison.realSabre
                  ? '⚠ SABRE REAL: revisa cada comando. Si el flujo estricto no incluye ER, usa “Guardar cambios (ER)” dentro de la vista elegida.'
                  : 'Las vistas usan llamadas Sabre simuladas. El resultado de IA se normaliza igual que en el flujo actual.'}
              </span>
              {comparison.error ? <span className="sst-comparison-error">⚠ {comparison.error}</span> : null}
              {comparison.busy ? (
                <button type="button" className="ia-btn ghost mini ia-stop" onClick={detenerProceso}>Detener comparación</button>
              ) : (
                <div className="sst-comparison-foot-actions">
                  <button type="button" className="ia-btn ghost" onClick={downloadSstComparisonReport}>Descargar reporte</button>
                  {comparison.realSabre ? (
                    <button type="button" className="ia-btn ghost" onClick={() => setComparisonSabreMode(false)}>Volver a Sabre simulado</button>
                  ) : (
                    <>
                      <button type="button" className="ia-btn ghost" onClick={() => compareSstGeneration(file)}>Volver a ejecutar agentes</button>
                      <button type="button" className="ia-btn primary danger" onClick={() => { setComparisonRealAccepted(false); setComparisonRealConfirmOpen(true) }}>Probar con Sabre real</button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {comparisonRealConfirmOpen ? (
        <div className="sst-real-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="sst-real-confirm-title">
          <div className="sst-real-confirm-modal">
            <div className="sst-real-confirm-icon">⚠</div>
            <h3 id="sst-real-confirm-title">Conectar el comparador con Sabre real</h3>
            <p>
              Las dos vistas leerán el PNR abierto usando tu sesión actual. Si dentro de cualquiera presionas ejecutar o confirmar,
              sus comandos podrán agregar, modificar o eliminar información y un <code>ER</code> podrá guardar los cambios.
            </p>
            <label className="sst-real-confirm-check">
              <input type="checkbox" checked={comparisonRealAccepted} onChange={(event) => setComparisonRealAccepted(event.target.checked)} />
              Entiendo que esta prueba puede modificar la reservación activa.
            </label>
            <div className="sst-real-confirm-actions">
              <button type="button" className="ia-btn ghost" onClick={() => setComparisonRealConfirmOpen(false)}>Cancelar</button>
              <button
                type="button"
                className="ia-btn primary danger"
                disabled={!comparisonRealAccepted}
                onClick={() => setComparisonSabreMode(true)}
              >
                Conectar con Sabre real
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {previewDraft ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15,23,42,.62)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
          }}
        >
          <div style={{ width: 'min(1180px, 96vw)', maxHeight: '92vh', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>
                  {previewDraft.prototype
                    ? (previewDraft.prototypeRuntime ? 'Ejecución SST simulada · sin IA' : 'Vista previa SST determinista · sin IA')
                    : tr('previewTitle')}
                </div>
                <strong>{humanizeScriptName(previewDraft.title)}</strong>
              </div>
              <button type="button" className="ia-btn ghost mini" onClick={previewDraft.prototype ? closePreviewDraft : stashCurrentDraft} disabled={Boolean(previewBusy)}>
                {tr('close')}
              </button>
            </div>
            <div style={{ height: '62vh', background: '#f8fafc', overflow: previewDraft.prototype && !previewDraft.prototypeRuntime ? 'auto' : 'hidden' }}>
              {previewDraft.prototype && !previewDraft.prototypeRuntime ? (
                <SstInterpreterPreview
                  ir={previewDraft.ir || {}}
                  logicJson={previewDraft.logicJson || {}}
                  fileName={previewDraft.title}
                />
              ) : (
                <iframe
                  title="Vista previa del script generado"
                  src={previewDraft.url}
                  sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
                  style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: 14, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {previewDraft.prototype ? (
                  <>
                    Intérprete SST seguro · {previewDraft.prototypeStats?.sourceForms || 0} ventanas fuente · profundidad{' '}
                    {previewDraft.prototypeStats?.maxMenuDepth || 0} · {previewDraft.prototypeStats?.logicBlocks || 0} bloques conservados
                  </>
                ) : (
                  <>
                    UUID: <code>{previewDraft.uuid}</code> - Usuario: <code>{previewDraft.session?.username}</code> - PCC:{' '}
                    <code>{previewDraft.session?.pcc}</code>
                  </>
                )}
              </span>
              {error && previewDraft && !reviewConfirmOpen ? (
                <span style={{ color: '#b91c1c', fontSize: 12 }}>
                  {error}
                </span>
              ) : null}
              {previewDraft.prototype ? (
                mockModeEnabled ? (
                  <div className="dashboard-preview-actions">
                    <span style={{ fontSize: 12, color: '#92400e' }}>
                      Las llamadas Sabre están simuladas; todavía no se puede publicar.
                    </span>
                    <button type="button" className="ia-btn primary dashboard-preview-action-btn" onClick={togglePrototypeRuntime}>
                      {previewDraft.prototypeRuntime ? 'Volver a estructura' : 'Probar ejecución simulada'}
                    </button>
                    <button type="button" className="ia-btn ghost dashboard-preview-action-btn" onClick={handleSendToReview}>
                      Enviar a DEV
                    </button>
                  </div>
                ) : null
              ) : <div className="dashboard-preview-actions">
                <button
                  type="button"
                  className="ia-btn ghost dashboard-preview-action-btn"
                  onClick={handleSendToReview}
                  disabled={Boolean(previewBusy)}
                  aria-busy={previewBusy === 'review' ? 'true' : 'false'}
                >
                  {previewBusy === 'review' ? <span className="dashboard-btn-spinner" aria-hidden="true" /> : null}
                  {previewBusy === 'review' ? tr('sending') : tr('sendReview')}
                </button>
                <button
                  type="button"
                  className="ia-btn primary dashboard-preview-action-btn"
                  onClick={handlePublishForMe}
                  disabled={Boolean(previewBusy)}
                  aria-busy={previewBusy === 'publish' ? 'true' : 'false'}
                >
                  {previewBusy === 'publish' ? <span className="dashboard-btn-spinner" aria-hidden="true" /> : null}
                  {previewBusy === 'publish' ? tr('publishing') : tr('publishForMe')}
                </button>
              </div>}
            </div>
          </div>
        </div>
      ) : null}
      {reviewConfirmOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(15,23,42,.48)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
          }}
        >
          <div style={{ width: 'min(460px, 94vw)', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,.35)' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <strong>{tr('reviewModalTitle')}</strong>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                {tr('reviewModalHelp')}
              </p>
            </div>
            <div style={{ padding: 16 }}>
              <label htmlFor="dashboard-review-feedback-confirm" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                {tr('reviewReason')}
              </label>
              <textarea
                id="dashboard-review-feedback-confirm"
                value={reviewFeedback}
                onChange={(event) => setReviewFeedback(event.target.value)}
                disabled={Boolean(previewBusy)}
                placeholder={tr('reviewPlaceholder')}
                rows={5}
                autoFocus
                style={{
                  width: '100%',
                  resize: 'vertical',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  padding: '9px 10px',
                  fontSize: 13,
                  lineHeight: 1.4,
                }}
              />
              {error && previewDraft ? (
                <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>
                  {error}
                </div>
              ) : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 14, borderTop: '1px solid #e5e7eb', background: '#fff' }}>
              <button type="button" className="ia-btn ghost" onClick={() => { setReviewConfirmOpen(false); setError('') }} disabled={Boolean(previewBusy)}>
                {tr('cancel')}
              </button>
              <button
                type="button"
                className="ia-btn primary dashboard-preview-action-btn"
                onClick={confirmSendToReview}
                disabled={Boolean(previewBusy)}
                aria-busy={previewBusy === 'review' ? 'true' : 'false'}
              >
                {previewBusy === 'review' ? <span className="dashboard-btn-spinner" aria-hidden="true" /> : null}
                {previewBusy === 'review' ? tr('sending') : tr('confirmSend')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  )
}

function IASpark() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 0l1.6 4.4L14 6l-4.4 1.6L8 12 6.4 7.6 2 6l4.4-1.6L8 0Zm5.2 8.2.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z"
      />
    </svg>
  )
}

export default BostonIAPanel
